/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from './supabase';
import * as XLSX from 'xlsx';
import TrendChart from './components/price/TrendChart';
import ShimmerRow from './components/price/ShimmerRow';
import PriceChangePill from './components/price/PriceChangePill';
import StatusBadge from './components/price/StatusBadge';
import ItemRow from './components/price/ItemRow';
import VolumeRow from './components/price/VolumeRow';
import VolumeDetailDrawer from './components/price/VolumeDetailDrawer';
import ItemDetailModal from './components/price/ItemDetailModal';
import { SegmentedPills } from './components/systemKit';
import VendorWatch from './components/VendorWatch';
import DataReview from './components/DataReview';
import { runShrinkflationDetection } from './lib/shrinkflation';
import { runParseGuardrails } from './lib/parseGuardrails';
import { computeLineDiff, computeInvoiceSummary } from './lib/invoiceDiff';
import { navigate } from './lib/navigate';

// hasWeightData: layered fallback because is_catch_weight is sparsely populated
// (only 11/290 items as of Apr 2026). Volume view needs a broader signal — anything
// we can convert to lbs, regardless of catch-weight flag. Price view continues to
// use is_catch_weight alone, since the question there is "is $/case unreliable?"
// which is exactly what the flag answers.
// See DESIGN_SYSTEM.md §Three concepts.
function hasWeightData(item, linesForItem) {
  if (item?.is_catch_weight) return true;
  if (item?.weight_per_case != null) return true;
  if (linesForItem?.some?.(l => l.total_weight != null)) return true;
  return false;
}

const CATEGORIES = ['Proteins', 'Produce', 'Dairy', 'Dry Goods', 'Beverages', 'Seafood', 'Desserts', 'Other'];

const PRICE_CHECK_SYSTEM_PROMPT = `You are a food cost advisor for an independent restaurant. Analyze the provided ingredient price history and return actionable recommendations.

DATA CONTRACT — follow this strictly, it prevents false alarms:
- Each item lists price per unit (/lb or /case) on each invoice, plus a pre-computed Trend %. The Trend % is the real price movement. Use it. Do not recompute a "change" from the raw dollar amounts — that number is already correct and accounts for quantity/pack differences.
- Items marked "[catch-weight — priced per lb]" are sold by actual weight (meat). Their per-case dollar amount varies by pack weight and is NOT a price change. Only judge these items by the /lb trend.
- Items with <1% movement have already been filtered out. If an item is in the data, the trend is real.
- Never say "price jumped from $X to $Y" unless those two dollar amounts are the same unit (both /lb or both /case) for the same item.

Focus on:
1. Items with significant price INCREASES >5% (flag as risks)
2. Items with consistent downward trends (opportunities to lock in)
3. Items priced meaningfully above regional average

For each finding, provide:
- The item name and the exact trend % from the data
- What this means for the restaurant in dollar terms
- 1-2 specific, concrete actions the manager should take

Be direct and practical. Avoid generic advice. Keep each finding concise. Format with clear section headers.`;

export default function FoodPriceTracker({ companyId }) {
  // Data states
  const [foodItems, setFoodItems] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [priceHistory, setPriceHistory] = useState({}); // { itemId: [{ price, date }] }
  const [itemSpend, setItemSpend] = useState({}); // { itemId: totalSpendLast90Days }
  const [regionalData, setRegionalData] = useState({}); // { normalizedName: { avg, contributors, yourPrice, savings } }
  const [regionalReady, setRegionalReady] = useState(false); // eslint-disable-line no-unused-vars
  const [companyRegion, setCompanyRegion] = useState('Kansas City'); // pulled from company profile
  const [priceTrackerSyncEnabled, setPriceTrackerSyncEnabled] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [cleanupStatus, setCleanupStatus] = useState(''); // '', 'running', or a result message
  const [loading, setLoading] = useState(true);

  // Vendor Action Items
  const [showVendorActions, setShowVendorActions] = useState(false);
  const [actionItems, setActionItems] = useState([]);
  const [actionItemsLoading, setActionItemsLoading] = useState(false);
  const [generatingEmail, setGeneratingEmail] = useState(null); // vendor name being generated
  const [emailDraft, setEmailDraft] = useState(null); // { vendor, body }
  const [showOutcomeModal, setShowOutcomeModal] = useState(null); // action item being resolved
  const [outcomeForm, setOutcomeForm] = useState({ outcome: '', notes: '' });

  // Summary
  const [summary, setSummary] = useState({ total: 0, invoicesThisMonth: 0, biggestImpact: null, biggestDrop: null });

  // Price impact over a time range (default = last 90 days). Click the card to edit the dates.
  const [priceImpactRange, setPriceImpactRange] = useState(() => {
    const end = new Date().toISOString().split('T')[0];
    const start = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    return { start, end };
  });
  const [priceImpactEditing, setPriceImpactEditing] = useState(false);
  const [priceImpact, setPriceImpact] = useState({
    net: 0, increases: 0, decreases: 0, itemsUp: 0, itemsDown: 0, coveredLines: 0, skippedLines: 0, contributors: [], loading: true,
  });
  // Per-item spend within the selected priceImpactRange (recomputed when the range or company changes).
  // Drives the items table's "$ Spend / range" column and dollar-impact calc.
  const [rangeSpend, setRangeSpend] = useState({}); // { itemId: totalLineDollarsInRange }
  const [priceImpactExpanded, setPriceImpactExpanded] = useState(false);
  const [showAllUp, setShowAllUp] = useState(false);
  const [showAllDown, setShowAllDown] = useState(false);
  // Detail modal for "see →" clicks from the Price Impact card
  const [detailModalItemId, setDetailModalItemId] = useState(null);

  // Sidebar
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Filters
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [vendorFilter, setVendorFilter] = useState('All');
  const [sortBy, setSortBy] = useState('change');
  // View-mode toggle: Price (default) or Volume. Per DESIGN_SYSTEM.md §Three concepts —
  // Price and Volume are two lenses on the same item list; segmented pills per §Tabs.
  const [viewMode, setViewMode] = useState('price');
  // Volume view data. Aggregated from invoice_line_items, grouped by food_item_id, over
  // the selected priceImpactRange. Two parallel aggregates (current + prior) for the
  // "vs prior" column. Fetched only when viewMode === 'volume'.
  const [volumeData, setVolumeData] = useState({}); // { [food_item_id]: { curr: {qty,lbs}, prior: {qty,lbs}, sparkline: [...] } }
  const [volumeLoading, setVolumeLoading] = useState(false);
  // Vendor Watch — durable shrinkflation alerts. Per-status arrays separated for
  // tier rendering. activeByItemId/watchingByItemId enable O(1) lookups for
  // inline chip rendering on item rows.
  // Initialized to null (vs []) so the Vendor Watch / Data Review
  // collapsible sections can distinguish "still loading" (renders
  // "checking…" subhead) from "loaded with zero items" (renders muted
  // empty state). Reset to [] after first fetch resolves; existing
  // consumers downstream defensively use `(alerts || [])` patterns so
  // null is safe.
  const [alerts, setAlerts] = useState(null); // all rows in {watching, active}
  // Data Review — items flagged by parse guardrails. Already filtered to
  // needs_review = TRUE here. The DataReview component is responsible for
  // its own collapse-when-empty behavior.
  const [reviewItems, setReviewItems] = useState(null);
  // Volume detail drawer (Volume mode only). Carries the food_items row
  // record + the useWeight flag computed from the row at open time so the
  // drawer renders consistently with the row that triggered it.
  const [volumeDrawer, setVolumeDrawer] = useState(null); // { item, useWeight } | null
  const [compareOnly, setCompareOnly] = useState(false);
  const [directionFilter, setDirectionFilter] = useState('all'); // 'all' | 'up' | 'down'
  const [expandedItem, setExpandedItem] = useState(null);
  const [expandedHistoryEntries, setExpandedHistoryEntries] = useState(new Set());
  const [editingHistoryEntry, setEditingHistoryEntry] = useState(null); // `${itemId}-${i}` key
  const [editPriceValue, setEditPriceValue] = useState('');
  const [editQtyValue, setEditQtyValue] = useState('');
  const [editLineTotalValue, setEditLineTotalValue] = useState('');

  // Upload modal
  const [showUpload, setShowUpload] = useState(false);
  // Batch upload state. When the user picks >1 file we switch the modal into batch mode:
  // each file becomes a job processed by a small concurrency pool (3 in flight) so onboarding
  // users can dump a whole binder of invoices at once without piling up backend requests.
  const BATCH_CONCURRENCY = 3;
  const [batchJobs, setBatchJobs] = useState([]); // [{ id, file, status, itemCount, error }]
  const [batchRunning, setBatchRunning] = useState(false);
  const [uploadMode, setUploadMode] = useState('pdf'); // 'pdf' or 'csv'
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadVendor, setUploadVendor] = useState('');
  // Vendor list for picker UI. Fed by food_vendors (authoritative) UNIONed with distinct
  // vendor_name values from invoices so freshly-used names show even before food_vendors is synced.
  const [knownVendors, setKnownVendors] = useState([]);
  const [uploadDate, setUploadDate] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parseStep, setParseStep] = useState('');
  const [parsedItems, setParsedItems] = useState(null);
  const [parseError, setParseError] = useState('');
  const [rawResponse, setRawResponse] = useState('');
  const [saving, setSaving] = useState(false);

  // Invoice detail
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceLineItems, setInvoiceLineItems] = useState([]);

  // AI Price Check
  const [showPriceCheck, setShowPriceCheck] = useState(false);
  const [priceCheckLoading, setPriceCheckLoading] = useState(false);
  const [priceCheckResult, setPriceCheckResult] = useState('');
  const [priceCheckMeta, setPriceCheckMeta] = useState({ invoices: 0, weeks: 0 });

  const fileInputRef = useRef(null);
  const dropRef = useRef(null);

  // ── Data fetching ──

  const fetchData = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    const [itemsRes, invoicesRes, companyRes, settingsRes, vendorsRes, distinctVendorsRes] = await Promise.all([
      supabase.from('food_items').select('*').eq('company_id', companyId).order('normalized_name'),
      supabase.from('invoices').select('*').eq('company_id', companyId).order('created_at', { ascending: false }).limit(10),
      supabase.from('companies').select('region, zip_code').eq('id', companyId).single(),
      supabase.from('food_cost_settings').select('price_tracker_sync_enabled').eq('company_id', companyId).maybeSingle(),
      supabase.from('food_vendors').select('id, name').eq('company_id', companyId).order('name'),
      supabase.from('invoices').select('vendor_name').eq('company_id', companyId),
    ]);

    // Union vendor names from both tables (food_vendors + invoices.vendor_name), unique case-insensitive.
    const seen = new Set();
    const vendorList = [];
    (vendorsRes.data || []).forEach(v => {
      const key = (v.name || '').toLowerCase().trim();
      if (key && !seen.has(key)) { seen.add(key); vendorList.push(v.name); }
    });
    (distinctVendorsRes.data || []).forEach(r => {
      const key = (r.vendor_name || '').toLowerCase().trim();
      if (key && !seen.has(key)) { seen.add(key); vendorList.push(r.vendor_name); }
    });
    vendorList.sort((a, b) => a.localeCompare(b));
    setKnownVendors(vendorList);
    const region = companyRes.data?.region || 'Kansas City';
    setCompanyRegion(region);
    setPriceTrackerSyncEnabled(!!settingsRes.data?.price_tracker_sync_enabled);
    const items = itemsRes.data || [];
    const invs = invoicesRes.data || [];
    setFoodItems(items);
    setInvoices(invs);

    // Fetch price history with dates + line item spend + regional data
    const ninetyDaysAgo = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const [historyRes, spendRes, regionalRes] = await Promise.all([
      items.length > 0
        ? supabase.from('item_price_history').select('food_item_id, unit_price, invoice_date, vendor_name, case_price, price_per_lb, unit, invoice_id').eq('company_id', companyId).order('invoice_date', { ascending: true })
        : Promise.resolve({ data: [] }),
      items.length > 0
        ? supabase.from('invoice_line_items').select('food_item_id, line_total').eq('company_id', companyId).gte('invoice_date', ninetyDaysAgo)
        : Promise.resolve({ data: [] }),
      supabase.from('regional_price_pool').select('normalized_name, unit_price, anon_contributor_id').eq('region', region).gte('invoice_date', ninetyDaysAgo),
    ]);

    // Also fetch line items for enriching history with qty/total/pack
    const { data: allLineItems } = items.length > 0
      ? await supabase.from('invoice_line_items').select('food_item_id, invoice_id, quantity, unit, line_total, pack_count, piece_weight').eq('company_id', companyId)
      : { data: [] };
    const lineItemLookup = {};
    (allLineItems || []).forEach(li => {
      if (li.food_item_id && li.invoice_id) lineItemLookup[`${li.food_item_id}-${li.invoice_id}`] = li;
    });

    // Group price history with dates
    const grouped = {};
    (historyRes.data || []).forEach(h => {
      if (!grouped[h.food_item_id]) grouped[h.food_item_id] = [];
      const li = lineItemLookup[`${h.food_item_id}-${h.invoice_id}`];
      grouped[h.food_item_id].push({
        price: Number(h.unit_price), date: h.invoice_date,
        vendor: h.vendor_name, case_price: h.case_price ? Number(h.case_price) : null,
        price_per_lb: h.price_per_lb ? Number(h.price_per_lb) : null,
        quantity: li?.quantity ? Number(li.quantity) : null,
        unit: li?.unit || h.unit,
        line_total: li?.line_total ? Number(li.line_total) : null,
        pack_info: li?.pack_count && li?.piece_weight ? `${li.pack_count}×${li.piece_weight}` : null,
        invoice_id: h.invoice_id,
      });
    });
    setPriceHistory(grouped);

    // Calculate total spend per item (last 90 days)
    const spend = {};
    (spendRes.data || []).forEach(li => {
      if (li.food_item_id && li.line_total) {
        spend[li.food_item_id] = (spend[li.food_item_id] || 0) + Number(li.line_total);
      }
    });
    setItemSpend(spend);

    // Process regional data — group by item, count unique contributors
    const regionalByItem = {};
    (regionalRes.data || []).forEach(r => {
      if (!r.normalized_name) return;
      if (!regionalByItem[r.normalized_name]) {
        regionalByItem[r.normalized_name] = { prices: [], contributors: new Set() };
      }
      regionalByItem[r.normalized_name].prices.push(Number(r.unit_price));
      if (r.anon_contributor_id) regionalByItem[r.normalized_name].contributors.add(r.anon_contributor_id);
    });

    const MIN_CONTRIBUTORS = 3;
    const regional = {};
    let anyReady = false;
    Object.entries(regionalByItem).forEach(([name, data]) => {
      const contributorCount = data.contributors.size;
      const otherPrices = data.prices; // all prices in pool (anonymized, includes yours)
      const avg = otherPrices.reduce((s, p) => s + p, 0) / otherPrices.length;
      const matchingItem = items.find(i => i.normalized_name === name);
      const yourPrice = matchingItem ? Number(matchingItem.current_price) : null;
      const isActive = contributorCount >= MIN_CONTRIBUTORS;
      if (isActive) anyReady = true;
      regional[name] = {
        avg: Math.round(avg * 100) / 100,
        contributors: contributorCount,
        yourPrice,
        isActive,
        diff: yourPrice ? Math.round(((yourPrice - avg) / avg) * 10000) / 100 : null,
      };
    });
    setRegionalData(regional);
    setRegionalReady(anyReady);

    // Summary — use dollar impact, not just %
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    const thisMonthInvoices = invs.filter(i => i.created_at >= monthStart).length;

    // Dollar impact = (price_change_pct / 100) * 90-day spend
    const withImpact = items
      .filter(i => i.price_change_pct != null && i.price_change_pct !== 0 && spend[i.id])
      .map(i => ({ ...i, dollarImpact: (i.price_change_pct / 100) * (spend[i.id] || 0) }));
    const costIncreases = withImpact.filter(i => i.dollarImpact > 0).sort((a, b) => b.dollarImpact - a.dollarImpact);
    const costDrops = withImpact.filter(i => i.dollarImpact < 0).sort((a, b) => a.dollarImpact - b.dollarImpact);

    setSummary({
      total: items.length,
      invoicesThisMonth: thisMonthInvoices,
      biggestImpact: costIncreases[0] || null,
      biggestDrop: costDrops[0] || null,
    });
    setLoading(false);
  }, [companyId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Price Tracker → Food Cost sync toggle ──
  async function savePriceTrackerSyncEnabled(nextValue) {
    setPriceTrackerSyncEnabled(nextValue);
    // Upsert on (company_id) — food_cost_settings.company_id has a unique index per migration
    const { data: existing } = await supabase
      .from('food_cost_settings')
      .select('id')
      .eq('company_id', companyId)
      .maybeSingle();
    if (existing) {
      await supabase.from('food_cost_settings').update({ price_tracker_sync_enabled: nextValue }).eq('id', existing.id);
    } else {
      await supabase.from('food_cost_settings').insert({ company_id: companyId, price_tracker_sync_enabled: nextValue });
    }
  }

  // Delete orphan / duplicate rows in food_invoices (source='price_tracker') that don't have a
  // matching row in Price Tracker's `invoices` table. Also recomputes food_cost_entries totals.
  async function cleanupSyncedInvoices() {
    if (!companyId) return;
    if (!window.confirm('Remove synced Food Cost rows that no longer match a Price Tracker invoice? (Manual Food Cost entries are not affected.)')) return;
    setCleanupStatus('running');
    try {
      // 1. Pull what Price Tracker currently has
      const { data: sourceInvs } = await supabase
        .from('invoices')
        .select('invoice_date, vendor_name, total_amount')
        .eq('company_id', companyId);
      // Build a multiset of "valid" signatures (date|vendorLower|totalRounded). Preserve counts
      // so if Price Tracker has 2 invoices on the same date with the same total, Food Cost keeps 2.
      const validCounts = new Map();
      (sourceInvs || []).forEach(r => {
        const sig = `${r.invoice_date}|${(r.vendor_name || '').toLowerCase()}|${Math.round(Number(r.total_amount || 0) * 100)}`;
        validCounts.set(sig, (validCounts.get(sig) || 0) + 1);
      });

      // 2. Pull existing synced Food Cost rows (need vendor name via join)
      const { data: synced } = await supabase
        .from('food_invoices')
        .select('id, date, total, vendor_id, created_at, food_vendors(name)')
        .eq('company_id', companyId)
        .eq('source', 'price_tracker')
        .order('created_at', { ascending: true });

      // 3. Walk the synced rows oldest → newest; keep up to validCounts[sig] matches, delete the rest
      const remainingCounts = new Map(validCounts);
      const toDelete = [];
      const affectedDates = new Set();
      (synced || []).forEach(r => {
        const vendorName = (r.food_vendors?.name || '').toLowerCase();
        const sig = `${r.date}|${vendorName}|${Math.round(Number(r.total || 0) * 100)}`;
        const remaining = remainingCounts.get(sig) || 0;
        if (remaining > 0) {
          remainingCounts.set(sig, remaining - 1);
        } else {
          toDelete.push(r.id);
          affectedDates.add(r.date);
        }
      });

      if (toDelete.length > 0) {
        await supabase.from('food_invoices').delete().in('id', toDelete);
      }

      // 4. Recompute food_cost_entries totals for every affected date
      for (const d of affectedDates) {
        const { data: remainingOnDate } = await supabase
          .from('food_invoices')
          .select('total')
          .eq('company_id', companyId)
          .eq('date', d);
        const newTotal = (remainingOnDate || []).reduce((s, r) => s + Number(r.total || 0), 0);
        const { data: entry } = await supabase
          .from('food_cost_entries')
          .select('id')
          .eq('company_id', companyId)
          .eq('date', d)
          .maybeSingle();
        if (entry) {
          if (newTotal > 0) {
            await supabase.from('food_cost_entries').update({ total: newTotal, updated_at: new Date().toISOString() }).eq('id', entry.id);
          } else {
            await supabase.from('food_cost_entries').delete().eq('id', entry.id);
          }
        }
      }

      setCleanupStatus(`Removed ${toDelete.length} orphaned / duplicate row(s).`);
    } catch (e) {
      setCleanupStatus(`Cleanup failed: ${e.message}`);
    }
  }

  // ── Price impact over a selected time range ──
  // For each line item in [start, end], compare the unit price against a baseline and
  // multiply by the quantity on that line. Baseline preference:
  //   1. Most recent price from BEFORE the range (true "what has this range cost me vs prior?")
  //   2. Fall back to the earliest purchase WITHIN the range if there's no prior history
  //      (so a range that contains the item's entire history still produces useful numbers).
  // Catch-weight items compare $/lb × total_weight instead so pack-weight variation doesn't
  // register as a cost change.
  const computePriceImpact = useCallback(async (start, end) => {
    if (!companyId || !start || !end) return;
    setPriceImpact(p => ({ ...p, loading: true }));

    const { data: lines } = await supabase
      .from('invoice_line_items')
      .select('food_item_id, quantity, unit_price, price_per_lb, total_weight, invoice_date')
      .eq('company_id', companyId)
      .gte('invoice_date', start)
      .lte('invoice_date', end)
      .order('invoice_date', { ascending: true });

    if (!lines || lines.length === 0) {
      setPriceImpact({ net: 0, increases: 0, decreases: 0, itemsUp: 0, itemsDown: 0, coveredLines: 0, skippedLines: 0, contributors: [], loading: false });
      return;
    }

    const affectedIds = Array.from(new Set(lines.map(l => l.food_item_id).filter(Boolean)));
    if (affectedIds.length === 0) {
      setPriceImpact({ net: 0, increases: 0, decreases: 0, itemsUp: 0, itemsDown: 0, coveredLines: 0, skippedLines: lines.length, contributors: [], loading: false });
      return;
    }

    const [{ data: itemsData }, { data: baselineRows }] = await Promise.all([
      supabase.from('food_items').select('id, is_catch_weight, normalized_name').in('id', affectedIds),
      supabase
        .from('item_price_history')
        .select('food_item_id, unit_price, price_per_lb, invoice_date')
        .in('food_item_id', affectedIds)
        .lt('invoice_date', start)
        .order('invoice_date', { ascending: false }),
    ]);

    const cwMap = {};
    const nameById = {};
    (itemsData || []).forEach(fi => {
      cwMap[fi.id] = !!fi.is_catch_weight;
      if (fi.normalized_name) nameById[fi.id] = fi.normalized_name;
    });
    // Fallback to the outer foodItems list (covers any edge-cases where the per-id fetch is stale).
    foodItems.forEach(fi => { if (!nameById[fi.id] && fi.normalized_name) nameById[fi.id] = fi.normalized_name; });

    // Tier 1: most recent pre-range entry per item
    const baseline = {};
    (baselineRows || []).forEach(r => {
      if (!baseline[r.food_item_id]) {
        baseline[r.food_item_id] = {
          case_price: r.unit_price != null ? Number(r.unit_price) : null,
          per_lb: r.price_per_lb != null ? Number(r.price_per_lb) : null,
          source: 'pre-range',
        };
      }
    });

    // Tier 2: fall back to earliest in-range purchase. Lines are already sorted ascending
    // by invoice_date, so the first time we see an item is its earliest purchase in the range.
    for (const line of lines) {
      if (!line.food_item_id || baseline[line.food_item_id]) continue;
      baseline[line.food_item_id] = {
        case_price: line.unit_price != null ? Number(line.unit_price) : null,
        per_lb: line.price_per_lb != null ? Number(line.price_per_lb) : null,
        source: 'first-in-range',
        skipLineDate: line.invoice_date, // the line that defined the baseline contributes 0 impact
      };
    }

    let increases = 0, decreases = 0, coveredLines = 0, skippedLines = 0;
    // Per-item running aggregate: delta, qty purchased in-range, latest price seen, catch-weight flag.
    const itemAgg = new Map();
    const baselineConsumed = new Set(); // prevent the defining line from counting as its own delta
    for (const line of lines) {
      const b = baseline[line.food_item_id];
      if (!b) { skippedLines++; continue; }
      if (b.source === 'first-in-range' && !baselineConsumed.has(line.food_item_id) && line.invoice_date === b.skipLineDate) {
        baselineConsumed.add(line.food_item_id);
        // Still track the baseline line's qty so the contributor row's "total cs" is accurate.
        const agg = itemAgg.get(line.food_item_id) || { delta: 0, qty: 0, latestPrice: null, latestPerLb: null, latestDate: null };
        agg.qty += Number(line.quantity) || 0;
        if (!agg.latestDate || line.invoice_date >= agg.latestDate) {
          agg.latestDate = line.invoice_date;
          agg.latestPrice = line.unit_price != null ? Number(line.unit_price) : agg.latestPrice;
          agg.latestPerLb = line.price_per_lb != null ? Number(line.price_per_lb) : agg.latestPerLb;
        }
        itemAgg.set(line.food_item_id, agg);
        skippedLines++;
        continue;
      }
      const isCW = !!cwMap[line.food_item_id];
      let delta = null;
      if (isCW && line.price_per_lb != null && b.per_lb != null && line.total_weight != null) {
        delta = (Number(line.price_per_lb) - b.per_lb) * Number(line.total_weight);
      } else if (line.unit_price != null && b.case_price != null && line.quantity != null) {
        delta = (Number(line.unit_price) - b.case_price) * Number(line.quantity);
      }
      if (delta == null || !isFinite(delta)) { skippedLines++; continue; }
      coveredLines++;
      if (delta > 0) increases += delta;
      else if (delta < 0) decreases += delta;
      const agg = itemAgg.get(line.food_item_id) || { delta: 0, qty: 0, latestPrice: null, latestPerLb: null, latestDate: null };
      agg.delta += delta;
      agg.qty += Number(line.quantity) || 0;
      if (!agg.latestDate || line.invoice_date >= agg.latestDate) {
        agg.latestDate = line.invoice_date;
        agg.latestPrice = line.unit_price != null ? Number(line.unit_price) : agg.latestPrice;
        agg.latestPerLb = line.price_per_lb != null ? Number(line.price_per_lb) : agg.latestPerLb;
      }
      itemAgg.set(line.food_item_id, agg);
    }

    // Build contributors list with enough info to render rows.
    const contributors = [];
    for (const [foodItemId, agg] of itemAgg.entries()) {
      if (Math.abs(agg.delta) < 0.5) continue; // ignore sub-50¢ rounding noise
      const b = baseline[foodItemId];
      const isCW = !!cwMap[foodItemId];
      contributors.push({
        food_item_id: foodItemId,
        name: nameById[foodItemId] || `Item #${foodItemId}`,
        delta: agg.delta,
        qty: agg.qty,
        baseline_price: isCW ? b?.per_lb : b?.case_price,
        current_price: isCW ? agg.latestPerLb : agg.latestPrice,
        // Per-lb values exposed explicitly so the Top Increases/Decreases Contributor
        // row can show "+$0.10/lb" as the primary delta for catch-weight items with
        // the aggregate $ delta as secondary context.
        baseline_per_lb: b?.per_lb != null ? Number(b.per_lb) : null,
        current_per_lb: agg.latestPerLb != null ? Number(agg.latestPerLb) : null,
        unit_label: isCW ? '/lb' : '/cs',
        is_catch_weight: isCW,
        baseline_source: b?.source || 'unknown',
      });
    }

    let itemsUp = 0, itemsDown = 0;
    for (const c of contributors) {
      if (c.delta > 0) itemsUp++;
      else if (c.delta < 0) itemsDown++;
    }

    setPriceImpact({
      net: increases + decreases,
      increases,
      decreases,
      itemsUp,
      itemsDown,
      coveredLines,
      skippedLines,
      contributors,
      loading: false,
    });
  }, [companyId, foodItems]);

  // Recompute whenever the range changes OR when fetchData refreshes core data
  // (foodItems.length is a convenient trigger — it changes after every import / delete).
  useEffect(() => {
    computePriceImpact(priceImpactRange.start, priceImpactRange.end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceImpactRange.start, priceImpactRange.end, computePriceImpact, foodItems.length]);

  // Fetch per-item $ spend over the selected range (drives the items-table spend column
  // and the dollar-impact column). Re-runs whenever the date range or core data changes.
  useEffect(() => {
    if (!companyId || !priceImpactRange.start || !priceImpactRange.end) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('invoice_line_items')
        .select('food_item_id, line_total')
        .eq('company_id', companyId)
        .gte('invoice_date', priceImpactRange.start)
        .lte('invoice_date', priceImpactRange.end);
      if (cancelled) return;
      const totals = {};
      (data || []).forEach(li => {
        if (li.food_item_id && li.line_total != null) {
          totals[li.food_item_id] = (totals[li.food_item_id] || 0) + Number(li.line_total);
        }
      });
      setRangeSpend(totals);
    })();
    return () => { cancelled = true; };
  }, [companyId, priceImpactRange.start, priceImpactRange.end, foodItems.length]);

  // ── Vendor Watch: load active + watching shrinkflation alerts for this company.
  //    Refreshes after dismissals and after invoice uploads. ──
  const fetchAlerts = useCallback(async () => {
    if (!companyId) return;
    const { data } = await supabase
      .from('shrinkflation_alerts')
      .select('*')
      .eq('company_id', companyId)
      .in('status', ['watching', 'active'])
      .order('first_detected_at', { ascending: false });
    setAlerts(data || []);
  }, [companyId]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  // Fetch review queue (food_items with needs_review = TRUE).
  const fetchReviewItems = useCallback(async () => {
    if (!companyId) return;
    const { data } = await supabase
      .from('food_items')
      .select('*')
      .eq('company_id', companyId)
      .eq('needs_review', true);
    setReviewItems(data || []);
  }, [companyId]);
  useEffect(() => { fetchReviewItems(); }, [fetchReviewItems]);

  // Quick lookup for the inline 'Needs review' chip on item rows. The chip's
  // expandable detail panel needs the full item record (review_reason etc.), so
  // we map id → row, not id → bool.
  const needsReviewByItemId = React.useMemo(() => {
    const m = {};
    (reviewItems || []).forEach(it => { m[it.id] = it; });
    return m;
  }, [reviewItems]);

  // Active-by-item lookup — drives the inline 'Shrinkflation' chip on rows.
  const activeAlertByItemId = React.useMemo(() => {
    const m = {};
    (alerts || []).filter(a => a.status === 'active').forEach(a => { m[a.food_item_id] = a; });
    return m;
  }, [alerts]);

  // Itemsbyid for VendorWatch card rendering (carries normalized_name + vendor_name).
  const itemsById = React.useMemo(() => {
    const m = {};
    (foodItems || []).forEach(fi => { m[fi.id] = fi; });
    return m;
  }, [foodItems]);

  // Dry-run handler exposed via the "Run alert check" button in VendorWatch.
  const handleDryRun = useCallback(async () => {
    if (!companyId) return { wouldFire: [] };
    const ids = (foodItems || []).map(fi => fi.id);
    return runShrinkflationDetection({ companyId, foodItemIds: ids, dryRun: true });
  }, [companyId, foodItems]);

  // ── Volume view: aggregate quantity + lbs per food_item over the selected range
  //    and the same-length prior range. Only fetched when viewMode === 'volume'. ──
  useEffect(() => {
    if (viewMode !== 'volume' || !companyId || !priceImpactRange.start || !priceImpactRange.end) return;
    let cancelled = false;
    (async () => {
      setVolumeLoading(true);
      const start = priceImpactRange.start;
      const end = priceImpactRange.end;
      // Same-length prior range immediately preceding [start, end].
      const days = Math.max(1, Math.round((new Date(end) - new Date(start)) / 86400000) + 1);
      const priorEndDate = new Date(start); priorEndDate.setDate(priorEndDate.getDate() - 1);
      const priorStartDate = new Date(priorEndDate); priorStartDate.setDate(priorStartDate.getDate() - (days - 1));
      const priorStart = priorStartDate.toISOString().split('T')[0];
      const priorEnd = priorEndDate.toISOString().split('T')[0];

      const { data } = await supabase
        .from('invoice_line_items')
        .select('food_item_id, invoice_date, quantity, total_weight')
        .eq('company_id', companyId)
        .gte('invoice_date', priorStart)
        .lte('invoice_date', end);
      if (cancelled) return;

      const agg = {};
      (data || []).forEach(li => {
        if (!li.food_item_id) return;
        const bucket = (li.invoice_date >= start && li.invoice_date <= end) ? 'curr'
                     : (li.invoice_date >= priorStart && li.invoice_date <= priorEnd) ? 'prior'
                     : null;
        if (!bucket) return;
        if (!agg[li.food_item_id]) agg[li.food_item_id] = { curr: { qty: 0, lbs: 0 }, prior: { qty: 0, lbs: 0 }, sparkline: [] };
        agg[li.food_item_id][bucket].qty += Number(li.quantity) || 0;
        agg[li.food_item_id][bucket].lbs += Number(li.total_weight) || 0;
        if (bucket === 'curr') {
          agg[li.food_item_id].sparkline.push({ date: li.invoice_date, value: Number(li.total_weight) || Number(li.quantity) || 0 });
        }
      });
      // Sort sparkline by date so the line renders left-to-right.
      Object.values(agg).forEach(r => r.sparkline.sort((a, b) => a.date.localeCompare(b.date)));
      setVolumeData(agg);
      setVolumeLoading(false);
    })();
    return () => { cancelled = true; };
  }, [viewMode, companyId, priceImpactRange.start, priceImpactRange.end]);

  // ── Price override (edit a parsed price on a historical invoice entry) ──

  async function savePriceOverride(item, entry, entryIndex) {
    const oldPrice = Number(entry.price);
    const oldQty = entry.quantity != null ? Number(entry.quantity) : null;
    const oldLineTotal = entry.line_total != null
      ? Number(entry.line_total)
      : (oldQty != null ? Math.round(oldPrice * oldQty * 100) / 100 : null);

    const round2 = (n) => Math.round(n * 100) / 100;

    // Parse inputs — treat a value as "provided" only if it differs from the current one.
    // All three fields are pre-filled with the current values; the user edits the one(s) they want.
    const parsed = (s) => {
      if (s === '' || s == null) return null;
      const n = parseFloat(s);
      return isFinite(n) ? n : NaN;
    };
    const pPrice = parsed(editPriceValue);
    const pQty = parsed(editQtyValue);
    const pLineTotal = parsed(editLineTotalValue);
    if (pPrice !== null && (Number.isNaN(pPrice) || pPrice <= 0)) { alert('Invalid price.'); return; }
    if (pQty !== null && (Number.isNaN(pQty) || pQty <= 0)) { alert('Invalid quantity (must be > 0).'); return; }
    if (pLineTotal !== null && (Number.isNaN(pLineTotal) || pLineTotal <= 0)) { alert('Invalid line total.'); return; }

    const priceProvided = pPrice !== null && pPrice !== oldPrice;
    const qtyProvided = pQty !== null && pQty !== oldQty;
    const lineTotalProvided = pLineTotal !== null && (oldLineTotal == null || pLineTotal !== oldLineTotal);

    if (!priceProvided && !qtyProvided && !lineTotalProvided) {
      setEditingHistoryEntry(null);
      setEditPriceValue(''); setEditQtyValue(''); setEditLineTotalValue('');
      return;
    }

    // Resolve final (newPrice, newQty, newLineTotal) by the rule:
    // "whatever you don't change stays the same; derive the rest from what you changed."
    let newPrice = oldPrice, newQty = oldQty, newLineTotal = oldLineTotal;
    const providedCount = (priceProvided ? 1 : 0) + (qtyProvided ? 1 : 0) + (lineTotalProvided ? 1 : 0);

    if (providedCount === 3) {
      // All three explicitly set — use as-is even if inconsistent.
      newPrice = pPrice; newQty = pQty; newLineTotal = pLineTotal;
    } else if (priceProvided && qtyProvided) {
      newPrice = pPrice; newQty = pQty;
      newLineTotal = round2(newPrice * newQty);
    } else if (priceProvided && lineTotalProvided) {
      newPrice = pPrice; newLineTotal = pLineTotal;
      newQty = newPrice > 0 ? newLineTotal / newPrice : oldQty;
    } else if (qtyProvided && lineTotalProvided) {
      newQty = pQty; newLineTotal = pLineTotal;
      newPrice = newQty > 0 ? round2(newLineTotal / newQty) : oldPrice;
    } else if (priceProvided) {
      // Only price changed → keep qty, recompute line_total.
      newPrice = pPrice;
      if (oldQty != null) newLineTotal = round2(newPrice * oldQty);
    } else if (qtyProvided) {
      // Only qty changed → keep line_total, recompute price.
      newQty = pQty;
      if (oldLineTotal != null && newQty > 0) {
        newPrice = round2(oldLineTotal / newQty);
        newLineTotal = oldLineTotal;
      } else {
        // No old line_total to anchor on — fall back to keeping price.
        newLineTotal = round2(oldPrice * newQty);
      }
    } else if (lineTotalProvided) {
      // Only line_total changed → keep qty, recompute price.
      newLineTotal = pLineTotal;
      if (oldQty != null && oldQty > 0) {
        newPrice = round2(newLineTotal / oldQty);
      }
    }

    const priceChanged = newPrice !== oldPrice;
    const qtyChanged = newQty !== oldQty;

    const weight = item.weight_per_case ? Number(item.weight_per_case) : null;
    const newPricePerLb = weight && weight > 0 ? Math.round((newPrice / weight) * 10000) / 10000 : null;

    // Price history is ordered ascending by invoice_date
    const history = priceHistory[item.id] || [];
    const isLatest = entryIndex === history.length - 1;
    const isSecondLatest = entryIndex === history.length - 2;

    const lineTotalChanged = (oldLineTotal == null && newLineTotal != null)
      || (oldLineTotal != null && newLineTotal != null && Math.abs(newLineTotal - oldLineTotal) > 0.005);
    const fmtQty = (q) => q != null ? (Number.isInteger(q) ? String(q) : q.toFixed(2)) : '—';
    const details = [
      `Item: ${item.normalized_name}`,
      `Invoice date: ${entry.date}${entry.vendor ? ` — ${entry.vendor}` : ''}`,
      priceChanged ? `Price/cs: $${oldPrice.toFixed(2)} → $${newPrice.toFixed(2)}` : `Price/cs (unchanged): $${oldPrice.toFixed(2)}`,
      qtyChanged ? `Qty: ${fmtQty(oldQty)} → ${fmtQty(newQty)} case(s)` : (newQty != null ? `Qty (unchanged): ${fmtQty(newQty)}` : null),
      lineTotalChanged
        ? `Line total: ${oldLineTotal != null ? '$' + oldLineTotal.toFixed(2) : '—'} → $${newLineTotal.toFixed(2)}`
        : (newLineTotal != null ? `Line total (unchanged): $${newLineTotal.toFixed(2)}` : null),
      newPricePerLb != null && priceChanged ? `New $/lb: $${newPricePerLb.toFixed(2)}` : null,
      isLatest
        ? 'This is the latest entry — current price, previous price, and % change on this item will be recalculated.'
        : (isSecondLatest && priceChanged)
          ? 'This is the second-most-recent entry — previous price and % change on this item will be recalculated.'
          : 'Older entry — food_items summary fields will not change.',
    ].filter(Boolean).join('\n');

    if (!window.confirm(`You're manually overriding parsed invoice data.\n\n${details}\n\nProceed?`)) return;

    if (!entry.invoice_id) {
      alert('Cannot update: missing invoice reference on this entry.');
      return;
    }

    // 1. Update item_price_history row (only has price-related fields, not qty)
    if (priceChanged) {
      const historyUpdate = { unit_price: newPrice, case_price: newPrice };
      if (newPricePerLb != null) historyUpdate.price_per_lb = newPricePerLb;
      const { error: hErr } = await supabase
        .from('item_price_history')
        .update(historyUpdate)
        .eq('food_item_id', item.id)
        .eq('invoice_id', entry.invoice_id);
      if (hErr) { alert('Failed to update price history: ' + hErr.message); return; }
    }

    // 2. Update invoice_line_items row (price, qty, line_total, price_per_lb)
    const liUpdate = {};
    if (priceChanged) liUpdate.unit_price = newPrice;
    if (qtyChanged) liUpdate.quantity = newQty;
    if (newLineTotal != null && (priceChanged || qtyChanged)) liUpdate.line_total = newLineTotal;
    if (priceChanged && newPricePerLb != null) liUpdate.price_per_lb = newPricePerLb;
    if (Object.keys(liUpdate).length > 0) {
      const { error: lErr } = await supabase
        .from('invoice_line_items')
        .update(liUpdate)
        .eq('food_item_id', item.id)
        .eq('invoice_id', entry.invoice_id);
      if (lErr) { alert('Failed to update line item: ' + lErr.message); return; }
    }

    // 3. Recalculate food_items summary fields if the price of the latest or second-latest entry changed.
    //    current_price = latest entry's price (post-edit)
    //    previous_price = second-most-recent entry's price (post-edit)
    //    price_change_pct = (current - previous) / previous * 100
    if (priceChanged && (isLatest || isSecondLatest)) {
      const effectivePrice = (idx) => idx === entryIndex ? newPrice : Number(history[idx].price);
      const latestIdx = history.length - 1;
      const prevIdx = history.length - 2;
      const newCurrent = effectivePrice(latestIdx);
      const newPrevious = prevIdx >= 0 ? effectivePrice(prevIdx) : null;
      const newCurrentPerLb = weight && weight > 0 ? Math.round((newCurrent / weight) * 10000) / 10000 : null;
      // For catch-weight items, case price varies with pack weight even when $/lb is flat.
      // Prefer $/lb when both entries have it so the displayed % change reflects actual price movement.
      const prevPerLb = prevIdx >= 0 ? (history[prevIdx].price_per_lb != null ? Number(history[prevIdx].price_per_lb) : null) : null;
      const newChangePct = (newCurrentPerLb != null && prevPerLb != null && prevPerLb > 0)
        ? Math.round(((newCurrentPerLb - prevPerLb) / prevPerLb) * 10000) / 100
        : (newPrevious && newPrevious > 0
          ? Math.round(((newCurrent - newPrevious) / newPrevious) * 10000) / 100
          : null);
      // Always write previous_price and price_change_pct — null clears stale values (e.g. after an invoice delete).
      const foodUpdate = {
        current_price: newCurrent,
        previous_price: newPrevious,
        price_change_pct: newChangePct,
      };
      if (newCurrentPerLb != null) foodUpdate.price_per_lb = newCurrentPerLb;
      const { error: fErr } = await supabase
        .from('food_items')
        .update(foodUpdate)
        .eq('id', item.id);
      if (fErr) { alert('Failed to update food item: ' + fErr.message); return; }
    }

    setEditingHistoryEntry(null);
    setEditPriceValue('');
    setEditQtyValue('');
    setEditLineTotalValue('');
    await fetchData();
  }

  // ── File handling ──

  // Shared PDF/image validator — returns true if the file is an acceptable invoice source.
  // Mirrors the checks in validateAndSetFile so batch mode rejects junk files before queueing.
  function isValidInvoiceFile(file) {
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/heic'];
    if (!validTypes.includes(file.type) && !file.name.match(/\.(pdf|jpg|jpeg|png|heic)$/i)) return false;
    if (file.size > 10 * 1024 * 1024) return false;
    return true;
  }

  function handleFileDrop(e) {
    e.preventDefault();
    const files = e.dataTransfer?.files;
    if (!files || files.length === 0) return;
    if (uploadMode === 'pdf' && files.length > 1) {
      const valid = Array.from(files).filter(isValidInvoiceFile);
      if (valid.length === 0) { setParseError('None of the dropped files are valid invoices (PDF/JPG/PNG/HEIC, <10MB).'); return; }
      startBatchFromFiles(valid);
      return;
    }
    validateAndSetFile(files[0]);
  }

  function handleFileSelect(e) {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    if (uploadMode === 'pdf' && files.length > 1) {
      const valid = Array.from(files).filter(isValidInvoiceFile);
      if (valid.length === 0) { setParseError('None of the selected files are valid invoices (PDF/JPG/PNG/HEIC, <10MB).'); return; }
      startBatchFromFiles(valid);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    validateAndSetFile(files[0]);
  }

  function handlePaste(e) {
    const items = e.clipboardData?.items;
    if (!items) return;
    const pastedFiles = [];
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const f = item.getAsFile();
        if (f) pastedFiles.push(new File([f], `pasted-invoice-${Date.now()}-${pastedFiles.length}.png`, { type: f.type }));
      }
    }
    if (pastedFiles.length === 0) return;
    e.preventDefault();
    if (pastedFiles.length > 1) {
      startBatchFromFiles(pastedFiles);
      return;
    }
    validateAndSetFile(pastedFiles[0]);
  }

  // Listen for paste when upload modal is open
  useEffect(() => {
    if (!showUpload) return;
    const handler = (e) => handlePaste(e);
    document.addEventListener('paste', handler);
    return () => document.removeEventListener('paste', handler);
  }, [showUpload]);

  function validateAndSetFile(file) {
    if (uploadMode === 'csv') {
      if (!file.name.match(/\.(csv|xlsx|xls)$/i)) {
        setParseError('Please upload a CSV or Excel file.');
        return;
      }
    } else {
      const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/heic'];
      if (!validTypes.includes(file.type) && !file.name.match(/\.(pdf|jpg|jpeg|png|heic)$/i)) {
        setParseError('Please upload a PDF, JPG, PNG, or HEIC file.');
        return;
      }
    }
    if (file.size > 10 * 1024 * 1024) {
      setParseError('File is too large (max 10MB). Please compress or use a smaller file.');
      return;
    }
    setUploadFile(file);
    setParseError('');
    setParsedItems(null);
    setRawResponse('');
  }

  function resetUploadModal() {
    setShowUpload(false);
    setUploadMode('pdf');
    setUploadFile(null);
    setUploadVendor('');
    setUploadDate('');
    setParsing(false);
    setParseStep('');
    setParsedItems(null);
    setParseError('');
    setRawResponse('');
    setSaving(false);
    setBatchJobs([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  // ── CSV/Excel parsing ──
  async function parseCSV() {
    if (!uploadFile) return;
    setParsing(true);
    setParseError('');
    setParseStep('Reading spreadsheet...');
    try {
      const data = await uploadFile.arrayBuffer();
      const workbook = XLSX.read(data);
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      // Read as 2D array so we can auto-detect the header row (SendItSheets exports
      // prefix the real data with 18+ rows of invoice metadata & page markers)
      const grid = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: null });
      if (grid.length === 0) throw new Error('No data found in spreadsheet');

      // Strip SendItSheets' :unselected: / :selected: tokens from every cell
      const cleanCell = (v) => {
        if (v == null) return null;
        const s = String(v).trim();
        if (s === '' || s === ':unselected:' || s === ':selected:') return null;
        return s.replace(/:unselected:/g, '').replace(/:selected:/g, '').trim() || null;
      };

      setParseStep('Detecting columns...');
      // Score each row by how many canonical column keywords it contains
      const HEADER_KEYWORDS = ['item', 'qty', 'quantity', 'description', 'desc', 'price', 'cost', 'amount', 'total', 'pack', 'size', 'unit', 'code', 'sku', 'weight'];
      const scoreRow = (row) => {
        const labels = row.map(c => (c == null ? '' : String(c).toLowerCase().trim()));
        const hits = new Set();
        labels.forEach(l => HEADER_KEYWORDS.forEach(k => { if (l && l === k) hits.add(k); }));
        return hits.size;
      };
      let headerIdx = -1;
      let bestScore = 0;
      for (let i = 0; i < Math.min(grid.length, 50); i++) {
        const s = scoreRow(grid[i] || []);
        if (s > bestScore && s >= 3) { bestScore = s; headerIdx = i; }
      }
      if (headerIdx === -1) throw new Error('Could not find column headers (need at least 3 of: Item, Qty, Description, Price, Amount, Pack, Size...)');

      const headers = (grid[headerIdx] || []).map(h => (h == null ? '' : String(h).toLowerCase().trim()));

      // Column matching: prefer whole-word matches (split on non-word chars) over naive includes().
      // This prevents traps like "memo code" stealing the "code" column from "item number", or
      // "order date" matching "qty" via "ord".
      const headerWords = headers.map(h => (h || '').split(/[^a-z0-9#]+/i).filter(Boolean));
      const findByWord = (...wants) => {
        const wantSet = new Set(wants.map(w => w.toLowerCase()));
        for (let i = 0; i < headerWords.length; i++) {
          if (headerWords[i].some(w => wantSet.has(w))) return i;
        }
        return -1;
      };

      // Code column: "Item Number", "Item #", "Item", "Product Code", "SKU" — avoid "Memo Code"
      const codeIdx = (() => {
        // Priority 1: any two-word header that pairs an item/product/sku token with num/number/code
        for (let i = 0; i < headerWords.length; i++) {
          const w = headerWords[i];
          const hasItem = w.some(x => ['item', 'product', 'sku', 'prod'].includes(x));
          const hasNum = w.some(x => ['number', 'num', 'no', '#', 'code', 'id'].includes(x));
          if (hasItem && hasNum) return i;
        }
        // Priority 2: single-word "item" / "sku"
        for (let i = 0; i < headerWords.length; i++) {
          if (headerWords[i].length === 1 && ['item', 'sku'].includes(headerWords[i][0])) return i;
        }
        // Priority 3: bare "code" but not preceded by something else
        for (let i = 0; i < headers.length; i++) if (headers[i] === 'code' || headers[i] === '#') return i;
        return -1;
      })();

      // Description column: "Description" exact first. Avoid "Customer Name".
      const descIdx = (() => {
        for (let i = 0; i < headers.length; i++) if (headers[i] === 'description' || headers[i] === 'desc') return i;
        for (let i = 0; i < headerWords.length; i++) if (headerWords[i].includes('description') || headerWords[i].includes('desc')) return i;
        // Last resort: bare "name" or "item name"
        for (let i = 0; i < headerWords.length; i++) {
          const w = headerWords[i];
          if ((w.includes('item') || w.includes('product')) && w.includes('name')) return i;
        }
        return -1;
      })();

      // Qty: prefer "Quantity Shipped" / "Shp Qty" over "Quantity Ordered".
      const qtyIdx = (() => {
        for (let i = 0; i < headerWords.length; i++) {
          const w = headerWords[i];
          if ((w.includes('quantity') || w.includes('qty') || w.includes('shp')) && (w.includes('shipped') || w.includes('ship') || w.includes('shp'))) return i;
        }
        for (let i = 0; i < headers.length; i++) if (headers[i] === 'qty' || headers[i] === 'quantity') return i;
        for (let i = 0; i < headerWords.length; i++) {
          if (headerWords[i].includes('quantity') || headerWords[i].includes('qty')) return i;
        }
        return findByWord('ord');
      })();

      const unitIdx = findByWord('unit');
      const packIdx = (() => {
        for (let i = 0; i < headers.length; i++) if (headers[i] === 'pack') return i;
        return findByWord('pack');
      })();
      const sizeIdx = (() => {
        for (let i = 0; i < headers.length; i++) if (headers[i] === 'size') return i;
        return findByWord('size');
      })();

      // Unit price: "Net Price", "Unit Price", "Price", "Cost". Avoid "Price Book Header".
      const priceIdx = (() => {
        for (let i = 0; i < headerWords.length; i++) {
          const w = headerWords[i];
          // Exclude any price header that looks like a categorical label (book/header/description).
          if (w.includes('book') || w.includes('header')) continue;
          if ((w.includes('net') || w.includes('unit') || w.includes('case')) && w.includes('price')) return i;
        }
        for (let i = 0; i < headerWords.length; i++) {
          const w = headerWords[i];
          if (w.includes('book') || w.includes('header')) continue;
          if (w.length <= 2 && (w.includes('price') || w.includes('cost'))) return i;
        }
        return -1;
      })();

      // Line total: "Extended Net Price", "Amount", "Extended Price", "Ext Total", "Line Total".
      // Must exclude "Extended Catch Weight" (weight, not money).
      const totalIdx = (() => {
        for (let i = 0; i < headerWords.length; i++) {
          const w = headerWords[i];
          if (w.includes('weight')) continue;
          if ((w.includes('extended') || w.includes('ext')) && (w.includes('price') || w.includes('total') || w.includes('amount') || w.includes('net'))) return i;
        }
        for (let i = 0; i < headerWords.length; i++) {
          const w = headerWords[i];
          if (w.includes('line') && (w.includes('total') || w.includes('amount'))) return i;
        }
        for (let i = 0; i < headers.length; i++) if (headers[i] === 'amount' || headers[i] === 'total') return i;
        return -1;
      })();

      // Weight: prefer a numeric weight column like "Extended Catch Weight" or "Total Weight".
      // Reject "Catch Weight Code" (single-letter Y/N flag).
      const weightIdx = (() => {
        for (let i = 0; i < headerWords.length; i++) {
          const w = headerWords[i];
          if (w.includes('code') || w.includes('flag')) continue;
          if (w.includes('weight')) return i;
        }
        return -1;
      })();

      // Additionally detect a boolean "catch weight" flag column (Y/N).
      const catchWeightFlagIdx = (() => {
        for (let i = 0; i < headerWords.length; i++) {
          const w = headerWords[i];
          if (w.includes('catch') && w.includes('weight') && (w.includes('code') || w.includes('flag'))) return i;
        }
        return -1;
      })();

      setParseStep('Processing items...');

      // Column index for "Invoice Date" if present (Springfield Grocer format stores it per row as YYYYMMDD)
      const invoiceDateIdx = (() => {
        for (let i = 0; i < headerWords.length; i++) {
          const w = headerWords[i];
          if (w.includes('invoice') && w.includes('date')) return i;
        }
        return -1;
      })();
      // Column index for vendor/distributor if present. "Customer Name" is the buyer, not the distributor —
      // skip it. Look for Distributor/Supplier/Vendor columns instead.
      const vendorIdx = (() => {
        for (let i = 0; i < headerWords.length; i++) {
          const w = headerWords[i];
          if (w.includes('distributor') || w.includes('supplier') || w.includes('vendor')) return i;
        }
        return -1;
      })();

      const parseDateCell = (val) => {
        if (val == null) return null;
        const s = String(val).trim();
        // ISO YYYY-MM-DD
        let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (m) return `${m[1]}-${m[2]}-${m[3]}`;
        // YYYYMMDD (Springfield Grocer) — could arrive as number or string
        m = s.match(/^(\d{4})(\d{2})(\d{2})$/);
        if (m) {
          const mo = parseInt(m[2], 10), d = parseInt(m[3], 10);
          if (mo >= 1 && mo <= 12 && d >= 1 && d <= 31) return `${m[1]}-${m[2]}-${m[3]}`;
        }
        // M/D/YY or M-D-YYYY etc.
        m = s.match(/^(\d{1,2})[\s/-](\d{1,2})[\s/-](\d{2,4})/);
        if (m) {
          const mo = parseInt(m[1], 10), d = parseInt(m[2], 10);
          let y = parseInt(m[3], 10);
          if (y < 100) y += 2000;
          if (mo >= 1 && mo <= 12 && d >= 1 && d <= 31 && y >= 2000 && y <= 2100) {
            return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
          }
        }
        return null;
      };

      // Date-from-row: check the first data row's Invoice Date column if available.
      // Fallback: scan pre-header metadata rows for "Date: ..." (SendItSheets format).
      const detectInvoiceDate = () => {
        if (invoiceDateIdx >= 0) {
          for (let i = headerIdx + 1; i < Math.min(grid.length, headerIdx + 5); i++) {
            const r = grid[i] || [];
            const d = parseDateCell(r[invoiceDateIdx]);
            if (d) return d;
          }
        }
        const dateRe = /date[:\s]+(\d{1,2})[\s/-](\d{1,2})[\s/-](\d{2,4})/i;
        for (let i = 0; i < headerIdx; i++) {
          for (const cell of grid[i] || []) {
            if (cell == null) continue;
            const m = String(cell).match(dateRe);
            if (m) {
              const mo = parseInt(m[1], 10), d = parseInt(m[2], 10);
              let y = parseInt(m[3], 10);
              if (y < 100) y += 2000;
              if (mo >= 1 && mo <= 12 && d >= 1 && d <= 31 && y >= 2000 && y <= 2100) {
                return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
              }
            }
          }
        }
        return null;
      };
      const date = uploadDate || detectInvoiceDate() || new Date().toISOString().split('T')[0];

      // Vendor: upload form > detected distributor column > empty (user can fill in review)
      const detectVendor = () => {
        if (vendorIdx >= 0) {
          for (let i = headerIdx + 1; i < Math.min(grid.length, headerIdx + 5); i++) {
            const val = cleanCell((grid[i] || [])[vendorIdx]);
            if (val) return val;
          }
        }
        return '';
      };
      const vendor = uploadVendor || detectVendor() || '';

      // Clean + classify description
      const cleanDescription = (raw) => {
        if (!raw) return '';
        let s = String(raw);
        // Drop everything after a newline (catch-weight breakdowns, "CATEGORY RECAP...", etc.)
        s = s.split('\n')[0];
        // Strip a leading brand-code prefix: "PIONEER -GRAVY MIX" → "GRAVY MIX".
        // Require whitespace before the dash so we don't mangle real hyphens like "T-SHIRT".
        s = s.replace(/^[A-Z][A-Z0-9 ']{0,20}\s-\s?/, '');
        return s.trim();
      };

      // Keyword-based category inference. Priority order matters — Other (non-food) first
      // so "NAPKIN DISPENSER" doesn't match "DISP" to something edible; then seafood before
      // proteins so "FISH" doesn't match before "CHICKEN"; etc.
      // Order matters: more specific / non-food categories checked first so e.g. "COOKIE DOUGH PEANUT BUTTER"
      // lands in Desserts (via "cookie dough") not Dairy (via "butter"), and "STUFFING CHICKEN FLAVORED"
      // lands in Dry Goods (via "stuffing") not Proteins (via "chicken").
      const CATEGORY_KEYWORDS = [
        ['Other', /\b(napkin|towel|glove|bag|filter|pad scrubber|scrubber|spoon|fork|utensil|cup|plate|wipe|cleaner|t-shirt|dispenser|foil|film|wrap|liner|container|lid|straw|brush|sponge|scraper)\b/i],
        ['Desserts', /\b(cake|cookie|cookie dough|pie|pudding|ice cream|icing|cobbler|frosting|brownie|donut|muffin|pastry|dessert|shell pie|roll dough)\b/i],
        ['Dry Goods', /\b(flour|sugar|salt|rice|pasta|oil|sauce|gravy|mix|hominy|stuffing|breading|syrup|vinegar|coating|spice|seasoning|dressing|mayonnaise|mayo|mustard|ketchup|pickle|spray|coconut|entree|macaroni|noodle)\b/i],
        ['Seafood', /\b(swai|salmon|tuna|cod|crab|lobster|tilapia|shrimp|scallop|fish\b|perch|halibut)\b/i],
        ['Proteins', /\b(chicken|beef|pork|ham|steak|sirloin|chuck|ground beef|drumstick|tender|wing|bacon|sausage|turkey|veal|lamb|hot dog|pepperoni|meat|rib|brisket|egg\b|eggs)\b/i],
        ['Dairy', /\b(milk|cream(?!\s*cheese)?|cheese|butter|yogurt|buttermilk|cottage|sour cream|half.?and.?half|dairy)\b/i],
        ['Beverages', /\b(juice|soda|coffee|tea|water\b|drink|beverage|lemonade)\b/i],
        // Produce (fresh veggies/fruit). Frozen veggies also land here since there's no "Frozen" category.
        ['Produce', /\b(lettuce|tomato|onion|pepper|cucumber|carrot|potato|apple|lemon|orange|banana|cabbage|mushroom|spinach|broccoli|zucchini|squash|cauliflower|celery|garlic|ginger|jalapeno|avocado|okra|corn\b|peas\b|bean\b|beans\b|strawberry|blueberry|grape|melon|vegetable blend|fruit)\b/i],
      ];
      const inferCategory = (desc) => {
        if (!desc) return 'Other';
        for (const [cat, re] of CATEGORY_KEYWORDS) {
          if (re.test(desc)) return cat;
        }
        return 'Other';
      };

      // Skip patterns: non-item lines we should drop
      const NON_ITEM_RE = /^(DELIVERY FEE|SUB\s*TOTAL|INVOICE TOTAL|CATEGORY RECAP|FUEL SURCHARGE|TOTAL DUE)/i;

      // Walk every row below the header; per-row filters decide what's a valid item.
      // (A single product section can have a large blank gap before another section.)
      const dataRows = [];
      for (let i = headerIdx + 1; i < grid.length; i++) {
        const row = (grid[i] || []).map(cleanCell);
        const hasAny = row.some(c => c != null);
        if (!hasAny) continue;
        dataRows.push(row);
      }

      const items = [];
      for (const row of dataRows) {
        const code = codeIdx >= 0 ? row[codeIdx] : null;
        const descRaw = descIdx >= 0 ? (row[descIdx] || '') : '';
        const desc = cleanDescription(descRaw);
        const qtyRaw = qtyIdx >= 0 ? row[qtyIdx] : null;
        const unitRaw = unitIdx >= 0 ? row[unitIdx] : null;
        const packRaw = packIdx >= 0 ? row[packIdx] : null;
        const sizeRaw = sizeIdx >= 0 ? row[sizeIdx] : null;
        const priceRaw = priceIdx >= 0 ? row[priceIdx] : null;
        const totalRaw = totalIdx >= 0 ? row[totalIdx] : null;
        const weightRaw = weightIdx >= 0 ? row[weightIdx] : null;
        const catchFlagRaw = catchWeightFlagIdx >= 0 ? row[catchWeightFlagIdx] : null;

        // Skip junk lines — test against the first line of the description only
        // (catch-weight items may have trailing "CATEGORY RECAP..." on a later line)
        const firstDescLine = String(descRaw || '').split('\n')[0].trim();
        if (firstDescLine && NON_ITEM_RE.test(firstDescLine)) continue;
        // Need at least a product code OR a description AND a price/total
        const hasPriceInfo = parseFloat(priceRaw) > 0 || parseFloat(totalRaw) > 0;
        if (!hasPriceInfo) continue;
        if (!code && !desc) continue;

        const qty = parseFloat(qtyRaw) || 1;
        const lineTotal = parseFloat(totalRaw) || 0;
        const rawPrice = parseFloat(priceRaw) || 0;
        const weightCol = parseFloat(weightRaw);

        // Catch-weight detection:
        //  1. Explicit flag column (Springfield Grocer: "Catch Weight Code" = "Y"/"N")
        //  2. Heuristic: Price × Qty ≠ Amount AND Weight column has a real number
        const flagStr = catchFlagRaw ? String(catchFlagRaw).trim().toUpperCase() : '';
        const explicitCatchWeight = flagStr === 'Y' || flagStr === 'YES' || flagStr === 'TRUE' || flagStr === '1';
        const priceTimesQty = rawPrice * qty;
        const amountMismatch = lineTotal > 0 && rawPrice > 0 && Math.abs(priceTimesQty - lineTotal) / lineTotal > 0.1;
        const heuristicCatchWeight = amountMismatch && isFinite(weightCol) && weightCol > 0;
        const isCatchWeight = explicitCatchWeight || heuristicCatchWeight;

        // case_price: always derive from line_total / qty when both are present (most accurate).
        // For non-catch-weight items missing a line total, fall back to the price column directly.
        let casePrice;
        if (lineTotal && qty) {
          casePrice = Math.round((lineTotal / qty) * 10000) / 10000;
        } else {
          casePrice = rawPrice;
        }

        // Pack / size parsing
        const packStr = packRaw ? String(packRaw) : '';
        const sizeStr = sizeRaw ? String(sizeRaw) : '';
        let packCount = packStr ? (parseInt(packStr) || null) : null;
        let pieceWeight = sizeStr || null;
        let totalWeightLbs = null;
        if (packCount && sizeStr) {
          const ozMatch = sizeStr.match(/([\d.]+)\s*oz/i);
          const lbMatch = sizeStr.match(/([\d.]+)\s*(lb|#)/i);
          if (ozMatch) totalWeightLbs = Math.round(packCount * parseFloat(ozMatch[1]) / 16 * 100) / 100;
          else if (lbMatch) totalWeightLbs = Math.round(packCount * parseFloat(lbMatch[1]) * 100) / 100;
        }
        // For catch-weight items, the Weight column is total lbs across all cases → per-case = weight/qty
        if (isCatchWeight && isFinite(weightCol) && weightCol > 0 && qty > 0) {
          totalWeightLbs = Math.round((weightCol / qty) * 100) / 100;
        }
        const pricePerLb = totalWeightLbs && casePrice
          ? Math.round((casePrice / totalWeightLbs) * 10000) / 10000
          : (isCatchWeight && rawPrice > 0 && !amountMismatch ? null : (isCatchWeight && rawPrice > 0 && amountMismatch ? rawPrice : null));

        // Per-row invoice date if provided by the spreadsheet (Springfield Grocer stores it)
        const rowInvoiceDate = invoiceDateIdx >= 0 ? (parseDateCell((grid[headerIdx + 1 + dataRows.indexOf(row)] || [])[invoiceDateIdx]) || parseDateCell(row[invoiceDateIdx]) || date) : date;
        const rowVendor = vendorIdx >= 0 ? (cleanCell(row[vendorIdx]) || vendor) : vendor;

        items.push({
          product_code: code || null,
          raw_description: String(descRaw).split('\n')[0].trim(),
          normalized_name: desc || String(descRaw).split('\n')[0].trim(),
          category: inferCategory(desc || String(descRaw).split('\n')[0].trim()),
          quantity: qty,
          unit: unitRaw ? String(unitRaw).toLowerCase() : 'case',
          case_price: casePrice,
          line_total: lineTotal,
          pack_count: packCount,
          piece_weight: pieceWeight,
          total_weight_lbs: totalWeightLbs,
          price_per_lb: pricePerLb,
          is_catch_weight: isCatchWeight,
          vendor_name: rowVendor,
          invoice_date: rowInvoiceDate,
        });
      }

      if (items.length === 0) throw new Error('No valid line items found (every row was filtered as junk). Check the file structure.');

      // Create invoice record
      const totalAmount = items.reduce((sum, r) => sum + (Number(r.line_total) || 0), 0);
      const { data: invoiceRow, error: invError } = await supabase.from('invoices').insert({
        company_id: companyId,
        vendor_name: vendor,
        invoice_date: date,
        total_amount: totalAmount,
        source_type: 'csv',
        status: 'parsed',
      }).select().single();
      if (invError) throw new Error(`Failed to save invoice: ${invError.message}`);

      items.forEach(it => { it._invoiceId = invoiceRow.id; });

      setParsedItems(items);
      setParseStep('');
    } catch (err) {
      setParseError(err.message);
      setParseStep('');
    }
    setParsing(false);
  }

  // ── Invoice parsing ──

  // Pure(ish) parse helper: uploads the file, runs Textract, validates against known packs,
  // and creates the invoices row. Returns the validated items (each stamped with _invoiceId).
  // No React state mutation — safe to call from a batch worker. Throws on failure.
  async function parseFileToItems(file, { vendorHint = '', dateHint = '', onStep = () => {}, confirmDuplicate = false } = {}) {
    onStep('Uploading invoice...');
    const filePath = `${companyId}/${Date.now()}_${file.name}`;
    const { error: uploadError } = await supabase.storage.from('invoices').upload(filePath, file);
    if (uploadError) throw new Error(`Upload failed: ${uploadError.message}`);

    onStep('Reading invoice...');
    const base64 = await fileToBase64(file);

    onStep('Starting extraction...');
    const startRes = await fetch('/api/pdf-start', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pdf_base64: base64, filename: file.name }),
    });
    const startData = await startRes.json();
    if (!startRes.ok || !startData.jobId) {
      throw new Error('This tool is not available right now. Please check back soon.');
    }
    const { jobId, s3Key } = startData;

    onStep('Identifying items...');
    let items = null;
    let detectedVendor = '';
    let detectedDate = '';
    const pollStart = Date.now();
    const POLL_TIMEOUT_MS = 180000;
    let pollIndex = 0;
    while (Date.now() - pollStart < POLL_TIMEOUT_MS) {
      const wait = pollIndex < 5 ? 1000 : (pollIndex < 20 ? 2000 : 3000);
      await new Promise((r) => setTimeout(r, wait));
      pollIndex++;
      const pollRes = await fetch('/api/pdf-poll', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ jobId, s3Key }),
      });
      const pollData = await pollRes.json();
      if (!pollRes.ok) throw new Error('This tool is not available right now. Please check back soon.');
      if (pollData.status === 'failed') throw new Error('This tool is not available right now. Please check back soon.');
      if (pollData.status === 'succeeded') {
        items = pollData.items || [];
        detectedVendor = pollData.vendor || '';
        detectedDate = pollData.invoice_date || '';
        break;
      }
      const elapsedSec = Math.round((Date.now() - pollStart) / 1000);
      if (elapsedSec > 20) onStep(`Identifying items... (${elapsedSec}s)`);
    }
    if (!items) throw new Error('Extraction timed out. Please try again.');

    onStep('Normalizing prices...');
    if (!Array.isArray(items) || items.length === 0) throw new Error('No items found in invoice');

    const vendor = vendorHint || detectedVendor || items[0]?.vendor_name || '';
    const date = dateHint || detectedDate || items[0]?.invoice_date || new Date().toISOString().split('T')[0];
    items = items.map(item => ({
      ...item,
      vendor_name: vendor || item.vendor_name,
      invoice_date: date || item.invoice_date,
    }));

    // Duplicate check — in batch mode we just skip, in single-file mode we prompt.
    const invoiceNum = items[0]?.invoice_number || null;
    if (invoiceNum && vendor) {
      const { data: dup } = await supabase
        .from('invoices').select('id').eq('company_id', companyId)
        .eq('invoice_number', invoiceNum).eq('vendor_name', vendor).limit(1);
      if (dup && dup.length > 0) {
        if (confirmDuplicate) {
          if (!window.confirm(`An invoice #${invoiceNum} from ${vendor} already exists. Save anyway?`)) {
            const e = new Error('DUPLICATE_SKIPPED'); e.code = 'DUPLICATE_SKIPPED'; throw e;
          }
        } else {
          const e = new Error(`Duplicate of existing invoice #${invoiceNum} from ${vendor} — skipped`);
          e.code = 'DUPLICATE_SKIPPED';
          throw e;
        }
      }
    }

    const { data: invoiceRow, error: invError } = await supabase.from('invoices').insert({
      company_id: companyId,
      vendor_name: vendor,
      invoice_date: date,
      invoice_number: items[0]?.invoice_number || null,
      total_amount: items.reduce((sum, i) => sum + (i.line_total || 0), 0),
      source_type: 'upload',
      file_url: filePath,
      raw_claude_response: JSON.stringify(items).slice(0, 10000),
      status: 'parsed',
    }).select().single();
    if (invError) throw new Error(`Failed to save invoice: ${invError.message}`);

    onStep('Validating data...');
    const { data: knownItems } = await supabase
      .from('food_items')
      .select('product_code, pack_size, weight_per_case, normalized_name, is_catch_weight')
      .eq('company_id', companyId)
      .not('product_code', 'is', null);
    const knownPacks = {};
    (knownItems || []).forEach(ki => {
      if (ki.product_code && ki.pack_size) {
        knownPacks[ki.product_code] = {
          pack_size: ki.pack_size,
          weight: ki.weight_per_case,
          is_catch_weight: !!ki.is_catch_weight,
        };
      }
    });

    const validatedItems = items.map(item => {
      if (item.line_total && item.quantity && item.quantity > 0) {
        item.case_price = Math.round((item.line_total / item.quantity) * 10000) / 10000;
      }
      if (item.product_code && knownPacks[item.product_code]?.is_catch_weight) {
        item.is_catch_weight = true;
      }
      if (item.product_code && knownPacks[item.product_code]) {
        const known = knownPacks[item.product_code];
        const packMatch = known.pack_size?.match(/^(\d+)×(.+)$/);
        if (packMatch) {
          item.pack_count = parseInt(packMatch[1]);
          item.piece_weight = packMatch[2];
        }
        if (known.weight && !item.is_catch_weight) {
          item.total_weight_lbs = known.weight;
        }
        if (item.total_weight_lbs && item.case_price) {
          item.price_per_lb = Math.round((item.case_price / item.total_weight_lbs) * 10000) / 10000;
        }
        item._packVerified = true;
      }
      if (item.case_price && item.line_total && item.quantity) {
        const calcCase = item.line_total / item.quantity;
        if (Math.abs(item.case_price - calcCase) > 1) {
          item._warning = `Case price ($${item.case_price}) doesn't match line_total/qty ($${calcCase.toFixed(2)})`;
          item.case_price = Math.round(calcCase * 100) / 100;
        }
      }
      return { ...item, _invoiceId: invoiceRow.id };
    });

    return { invoiceRow, validatedItems };
  }

  async function parseInvoice() {
    if (!uploadFile) return;
    setParsing(true);
    setParseError('');
    setParsedItems(null);
    setRawResponse('');
    try {
      const { validatedItems } = await parseFileToItems(uploadFile, {
        vendorHint: uploadVendor,
        dateHint: uploadDate,
        onStep: setParseStep,
        confirmDuplicate: true,
      });
      setRawResponse(JSON.stringify(validatedItems.slice(0, 3), null, 2));
      setParsedItems(validatedItems);
      setParseStep('');
    } catch (err) {
      if (err.code !== 'DUPLICATE_SKIPPED') setParseError(err.message);
      setParseStep('');
    }
    setParsing(false);
  }

  function fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // ── Save parsed items ──

  // Pure helper: given parsed items (with _invoiceId stamped by parseFileToItems), writes
  // all DB rows (food_items, line items, price history, regional pool, optional Food Cost sync).
  // No state mutation — safe to call from a batch worker. Throws on failure.
  async function saveInvoiceItems(items) {
    if (!items || items.length === 0) return;
    const invoiceId = items[0]._invoiceId;
    const anonId = btoa(String(companyId)).replace(/=/g, '').slice(0, 12);
    const today = new Date().toISOString().split('T')[0];

      // 1. Fetch ALL existing food_items for this company in one query
      const { data: allFoodItems } = await supabase
        .from('food_items')
        .select('*')
        .eq('company_id', companyId);
      const existingItems = allFoodItems || [];

      // 2. Process items — build batch arrays
      const lineItemRows = [];
      const priceHistoryRows = [];
      const regionalRows = [];
      const foodItemUpdates = []; // { id, updates }
      const foodItemInserts = []; // new items to insert
      // Alias rows pair with food_item_aliases. Added in the 2026-04-24 dedup
      // migration. Each line-item that resolves to an existing canonical food_item
      // AND carries a vendor_name + product_code emits an alias upsert so the full
      // (canonical, raw_vendor_name, product_code) map is preserved.
      const aliasRows = []; // { canonical_item_id (or _tempRef), raw_vendor_name, product_code, raw_item_name }

      for (const item of items) {
        const nameWithUnit = item.unit ? `${item.normalized_name} (${item.unit})` : item.normalized_name;
        const invDate = item.invoice_date || today;

        if (item.unit_price == null && item.case_price == null && item.line_total == null) {
          lineItemRows.push({
            invoice_id: invoiceId, company_id: companyId,
            raw_description: item.raw_description, product_code: item.product_code,
            normalized_name: item.normalized_name, category: item.category,
            quantity: item.quantity, unit: item.unit, unit_price: item.unit_price || item.case_price,
            line_total: item.line_total, invoice_date: invDate, vendor_name: item.vendor_name,
          });
          continue;
        }

        // Normalize prices — handle both old (unit_price) and new (case_price) format
        const casePrice = item.case_price || item.unit_price || ((item.line_total && item.quantity && item.quantity > 0)
          ? Math.round((item.line_total / item.quantity) * 10000) / 10000
          : null);
        if (casePrice == null) {
          lineItemRows.push({
            invoice_id: invoiceId, company_id: companyId,
            raw_description: item.raw_description, product_code: item.product_code,
            normalized_name: nameWithUnit, category: item.category,
            quantity: item.quantity, unit: item.unit, unit_price: null,
            line_total: item.line_total, invoice_date: invDate, vendor_name: item.vendor_name,
          });
          continue;
        }
        const pricePerLb = item.price_per_lb || null;
        const normalizedPrice = casePrice;

        // Match existing food_item. Post-dedup (2026-04-24) the canonical key is
        // (company_id, product_code) alone — vendor_name OCR drift ("SGC" vs
        // "SGC\nFOODSERVICE" vs "TM\nSGC") no longer creates new canonical rows.
        // The raw vendor string is preserved on invoice_line_items.vendor_name and
        // recorded in food_item_aliases for cross-invoice attribution.
        let existing = null;
        if (item.product_code) {
          existing = existingItems.find(fi => fi.product_code === item.product_code);
        }
        if (!existing) {
          // Fallback only for items that lack a product_code entirely.
          existing = existingItems.find(fi =>
            fi.normalized_name?.toLowerCase() === nameWithUnit.toLowerCase()
          );
        }

        // Track food_item id — will be resolved after batch upserts
        let foodItemRef;
        if (existing) {
          const isCatchWeight = !!(item.is_catch_weight || existing.is_catch_weight);
          // For catch-weight items, food_items.weight_per_case is meaningless (varies per delivery).
          // Keep whatever was there so sorting / summaries don't break, but never overwrite with a
          // new per-invoice measurement.
          const nextWeightPerCase = isCatchWeight
            ? existing.weight_per_case
            : (item.total_weight_lbs || existing.weight_per_case);
          foodItemUpdates.push({
            id: existing.id,
            updates: {
              previous_price: existing.current_price,
              current_price: normalizedPrice,
              price_change_pct: existing.current_price
                ? Math.round(((normalizedPrice - existing.current_price) / existing.current_price) * 10000) / 100
                : null,
              price_per_lb: pricePerLb || existing.price_per_lb,
              pack_size: item.pack_count && item.piece_weight ? `${item.pack_count}×${item.piece_weight}` : existing.pack_size,
              weight_per_case: nextWeightPerCase,
              is_catch_weight: isCatchWeight,
              category: item.category || existing.category,
              product_code: item.product_code || existing.product_code,
              vendor_name: item.vendor_name || existing.vendor_name,
              last_updated: invDate,
            },
          });
          foodItemRef = existing.id;
          // Update in-memory so subsequent items in same batch see the new price
          existing.current_price = normalizedPrice;
          // Record the alias — captures the raw vendor string for this line even
          // though the canonical item is vendor-agnostic.
          if (item.product_code && item.vendor_name) {
            aliasRows.push({
              canonical_item_id: existing.id,
              company_id: companyId,
              raw_vendor_name: item.vendor_name,
              product_code: item.product_code,
              raw_item_name: item.raw_description || item.normalized_name || null,
              last_seen_at: new Date().toISOString(),
            });
          }
        } else {
          // Check if we already queued this item for insert in this batch
          const queued = foodItemInserts.find(fi =>
            (item.product_code && fi.product_code === item.product_code) ||
            fi.normalized_name === nameWithUnit
          );
          if (queued) {
            queued._price = normalizedPrice;
            foodItemRef = queued._tempRef;
          } else {
            const tempRef = `new_${foodItemInserts.length}`;
            foodItemInserts.push({
              _tempRef: tempRef,
              _price: normalizedPrice,
              company_id: companyId, normalized_name: nameWithUnit,
              category: item.category, unit: item.unit,
              current_price: normalizedPrice, product_code: item.product_code,
              vendor_name: item.vendor_name, last_updated: invDate,
              price_per_lb: pricePerLb,
              pack_size: item.pack_count && item.piece_weight ? `${item.pack_count}×${item.piece_weight}` : null,
              weight_per_case: item.is_catch_weight ? null : (item.total_weight_lbs || null),
              is_catch_weight: !!item.is_catch_weight,
            });
            foodItemRef = tempRef;
          }
          // Seed an alias for the new item too so day-zero attribution is captured.
          if (item.product_code && item.vendor_name) {
            aliasRows.push({
              canonical_item_id: foodItemRef, // may be a tempRef; resolved below
              company_id: companyId,
              raw_vendor_name: item.vendor_name,
              product_code: item.product_code,
              raw_item_name: item.raw_description || item.normalized_name || null,
              last_seen_at: new Date().toISOString(),
            });
          }
        }

        lineItemRows.push({
          invoice_id: invoiceId, company_id: companyId, food_item_id: foodItemRef,
          raw_description: item.raw_description, product_code: item.product_code,
          normalized_name: nameWithUnit, category: item.category,
          quantity: item.quantity, unit: item.unit, unit_price: casePrice,
          line_total: item.line_total, invoice_date: invDate, vendor_name: item.vendor_name,
          pack_count: item.pack_count || null, piece_weight: item.piece_weight || null,
          total_weight: item.total_weight_lbs || null, price_per_lb: pricePerLb,
        });

        priceHistoryRows.push({
          food_item_id: foodItemRef, company_id: companyId, invoice_id: invoiceId,
          vendor_name: item.vendor_name, invoice_date: invDate,
          unit_price: normalizedPrice, unit: item.unit,
          case_price: casePrice, price_per_lb: pricePerLb,
        });

        regionalRows.push({
          normalized_name: nameWithUnit, category: item.category,
          unit: item.unit, unit_price: casePrice,
          vendor_name: item.vendor_name, invoice_date: invDate,
          region: companyRegion, anon_contributor_id: anonId,
          price_per_lb: pricePerLb,
        });
      }

      // 3. Batch update existing food_items (parallel)
      await Promise.all(foodItemUpdates.map(u =>
        supabase.from('food_items').update(u.updates).eq('id', u.id)
      ));

      // 4. Batch insert new food_items and get their IDs
      const tempRefToId = {};
      if (foodItemInserts.length > 0) {
        const insertData = foodItemInserts.map(({ _tempRef, _price, ...rest }) => rest);
        const { data: created } = await supabase.from('food_items').insert(insertData).select();
        (created || []).forEach((row, i) => {
          tempRefToId[foodItemInserts[i]._tempRef] = row.id;
        });
      }

      // 5. Resolve temp refs to real IDs
      const resolveId = (ref) => typeof ref === 'string' && ref.startsWith('new_') ? tempRefToId[ref] : ref;
      lineItemRows.forEach(r => { if (r.food_item_id) r.food_item_id = resolveId(r.food_item_id); });
      priceHistoryRows.forEach(r => { r.food_item_id = resolveId(r.food_item_id); });
      // Resolve alias canonical_item_id tempRefs and collapse duplicates within the batch.
      const aliasByKey = new Map();
      aliasRows.forEach(a => {
        const canonicalId = resolveId(a.canonical_item_id);
        if (!canonicalId) return;
        const key = `${a.company_id}|${canonicalId}|${a.raw_vendor_name}|${a.product_code}`;
        const existing = aliasByKey.get(key);
        if (existing) {
          existing.last_seen_at = a.last_seen_at;
          existing.occurrence_count = (existing.occurrence_count || 1) + 1;
        } else {
          aliasByKey.set(key, { ...a, canonical_item_id: canonicalId, occurrence_count: 1 });
        }
      });
      const resolvedAliases = [...aliasByKey.values()];

      // 6. Batch insert everything in parallel
      await Promise.all([
        lineItemRows.length > 0 ? supabase.from('invoice_line_items').insert(lineItemRows) : Promise.resolve(),
        priceHistoryRows.length > 0 ? supabase.from('item_price_history').insert(priceHistoryRows.filter(r => r.food_item_id)) : Promise.resolve(),
        regionalRows.length > 0 ? supabase.from('regional_price_pool').insert(regionalRows) : Promise.resolve(),
        // Upsert aliases: on conflict (company_id, canonical_item_id, raw_vendor_name,
        // product_code), bump last_seen_at + occurrence_count. first_seen_at / defaults
        // come from the DB so an insert populates them and an upsert leaves them.
        resolvedAliases.length > 0
          ? supabase.from('food_item_aliases').upsert(resolvedAliases, {
              onConflict: 'company_id,canonical_item_id,raw_vendor_name,product_code',
              ignoreDuplicates: false,
            })
          : Promise.resolve(),
      ]);

      // 6b. Reconcile food_items summary from invoice_date-ordered history.
      // The updates at step 3 assumed "new import == most recent", but if the user
      // imports an older invoice after a newer one, current_price/previous_price/change_pct
      // would be wrong. Pull the remaining history sorted by invoice_date and recompute.
      const affectedIds = Array.from(new Set(priceHistoryRows.map(r => r.food_item_id).filter(Boolean)));
      if (affectedIds.length > 0) {
        const { data: affectedFoodItems } = await supabase
          .from('food_items')
          .select('id, weight_per_case')
          .in('id', affectedIds);
        const weightById = {};
        (affectedFoodItems || []).forEach(it => { weightById[it.id] = it.weight_per_case ? Number(it.weight_per_case) : null; });

        const { data: hist } = await supabase
          .from('item_price_history')
          .select('food_item_id, unit_price, price_per_lb, invoice_date')
          .in('food_item_id', affectedIds)
          .order('invoice_date', { ascending: true });

        const byItem = {};
        (hist || []).forEach(h => {
          if (!byItem[h.food_item_id]) byItem[h.food_item_id] = [];
          byItem[h.food_item_id].push(h);
        });

        const reconcileUpdates = affectedIds
          .filter(id => byItem[id] && byItem[id].length > 0)
          .map(id => {
            const arr = byItem[id];
            const latest = arr[arr.length - 1];
            const prev = arr.length >= 2 ? arr[arr.length - 2] : null;
            const newCurrent = Number(latest.unit_price);
            const newPrevious = prev ? Number(prev.unit_price) : null;
            const weight = weightById[id];
            const newPricePerLb = latest.price_per_lb != null
              ? Number(latest.price_per_lb)
              : (weight && weight > 0 ? Math.round((newCurrent / weight) * 10000) / 10000 : null);
            // Catch-weight safe: if both entries have $/lb, use $/lb for change % so pack-weight
            // variation doesn't look like a price change.
            const prevPerLb = prev && prev.price_per_lb != null ? Number(prev.price_per_lb) : null;
            const newChangePct = (newPricePerLb != null && prevPerLb != null && prevPerLb > 0)
              ? Math.round(((newPricePerLb - prevPerLb) / prevPerLb) * 10000) / 100
              : (newPrevious && newPrevious > 0
                ? Math.round(((newCurrent - newPrevious) / newPrevious) * 10000) / 100
                : null);
            return {
              id,
              updates: {
                current_price: newCurrent,
                previous_price: newPrevious,
                price_change_pct: newChangePct,
                price_per_lb: newPricePerLb,
              },
            };
          });

        await Promise.all(reconcileUpdates.map(u =>
          supabase.from('food_items').update(u.updates).eq('id', u.id)
        ));
      }

      // 6c. Run shrinkflation detection — affected items only. Wrapped in
      //     try/catch: a detection failure must never break the upload UX
      //     (per Volume Tracker Commit 3 spec). Logs full context to console
      //     on failure so it's debuggable but invisible to the user.
      try {
        if (affectedIds.length > 0) {
          await runShrinkflationDetection({ companyId, foodItemIds: affectedIds });
          // Refresh the alerts list so VendorWatch reflects new state.
          fetchAlerts();
        }
      } catch (detectionErr) {
        console.error('[shrinkflation] detection failed (non-fatal)', {
          companyId, foodItemIds: affectedIds,
          message: detectionErr?.message, stack: detectionErr?.stack,
        });
      }

      // 6d. Run parse guardrails on the affected items. Same try/catch
      //     contract as shrinkflation — a guardrail failure must never break
      //     upload UX. Refreshes the Data Review queue afterward.
      try {
        if (affectedIds.length > 0) {
          await runParseGuardrails({ companyId, foodItemIds: affectedIds });
          fetchReviewItems();
        }
      } catch (guardErr) {
        console.error('[parseGuardrails] detection failed (non-fatal)', {
          companyId, foodItemIds: affectedIds,
          message: guardErr?.message, stack: guardErr?.stack,
        });
      }

      // 6e. Compute parse_diff per line + parse_summary for the invoice.
      //     This is what powers the live notification toast + Invoice Review
      //     modal. Must run LAST in the upload pipeline so the realtime
      //     UPDATE on `invoices` (which fires when we stamp parse_summary +
      //     parse_summary_at + review_status='pending') triggers App.js
      //     after every other side-effect has settled.
      //
      //     Same try/catch contract as shrinkflation/parseGuardrails — a
      //     diff failure must never break upload UX. The operator still has
      //     a saved invoice; they just don't get the live alert.
      //
      //     See src/lib/invoiceDiff.js for diff classification rules.
      try {
        if (invoiceId && affectedIds.length > 0) {
          // Fetch the line items we just inserted (with their assigned ids
          // and resolved food_item_id values) so we can write parse_diff
          // back to each row.
          const { data: insertedLines } = await supabase
            .from('invoice_line_items')
            .select('id, invoice_id, food_item_id, vendor_name, invoice_date, quantity, unit_price, price_per_lb, pack_count, piece_weight, normalized_name, raw_description')
            .eq('invoice_id', invoiceId);

          // Fetch history for the affected food_items: last 90 days from
          // *prior* invoices (exclude the one we just wrote). This is the
          // baseline universe for both vendor-specific and rolling-90d
          // comparisons. Newest-first ordering — invoiceDiff.js helpers
          // assume that.
          const ninetyDaysAgo = new Date();
          ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
          const { data: historyRows } = await supabase
            .from('invoice_line_items')
            .select('id, invoice_id, food_item_id, vendor_name, invoice_date, unit_price, price_per_lb, pack_count, piece_weight')
            .eq('company_id', companyId)
            .in('food_item_id', affectedIds)
            .neq('invoice_id', invoiceId)
            .gte('invoice_date', ninetyDaysAgo.toISOString().split('T')[0])
            .order('invoice_date', { ascending: false });

          // Group history by food_item_id for O(1) lookup per line.
          const historyByItem = new Map();
          (historyRows || []).forEach(r => {
            if (!historyByItem.has(r.food_item_id)) historyByItem.set(r.food_item_id, []);
            historyByItem.get(r.food_item_id).push(r);
          });

          // Parent invoice for invoice_date context.
          const { data: parentInvoice } = await supabase
            .from('invoices')
            .select('id, invoice_date, vendor_name')
            .eq('id', invoiceId)
            .maybeSingle();

          // Compute diff per line. Skip lines without a food_item_id —
          // those failed matching upstream and have no comparison basis.
          const lineDiffsForSummary = [];
          const linesForSummary = [];
          const diffUpdates = []; // { id, parse_diff }
          for (const line of (insertedLines || [])) {
            if (!line.food_item_id) {
              lineDiffsForSummary.push(null);
              linesForSummary.push(line);
              continue;
            }
            const itemHistory = historyByItem.get(line.food_item_id) || [];
            const vendorHistory = itemHistory.filter(h => h.vendor_name === line.vendor_name);
            const diff = computeLineDiff(line, itemHistory, vendorHistory, parentInvoice);
            lineDiffsForSummary.push(diff);
            linesForSummary.push(line);
            diffUpdates.push({ id: line.id, parse_diff: diff });
          }

          // Bulk-write parse_diff per line. Doing N updates rather than a
          // single `upsert` because PostgREST's upsert semantics on jsonb
          // columns with the rest of the row absent are surprising — easier
          // to keep this explicit. N is small (typically <100 per invoice).
          await Promise.all(diffUpdates.map(u =>
            supabase.from('invoice_line_items').update({ parse_diff: u.parse_diff }).eq('id', u.id)
          ));

          // Roll-up summary + stamp on the invoice. The UPDATE on the
          // invoice row is what App.js's realtime subscription fires on —
          // this is the trigger for the live toast.
          const summary = computeInvoiceSummary(lineDiffsForSummary, linesForSummary);
          await supabase
            .from('invoices')
            .update({
              parse_summary: summary,
              parse_summary_at: new Date().toISOString(),
              review_status: 'pending',
            })
            .eq('id', invoiceId);
        }
      } catch (diffErr) {
        console.error('[invoiceDiff] computation failed (non-fatal)', {
          companyId, invoiceId,
          message: diffErr?.message, stack: diffErr?.stack,
        });
      }

      // ── Sync to Food Cost page tables ──
      // Gated by food_cost_settings.price_tracker_sync_enabled — Price Tracker is a separate
      // product line, Food Cost only pulls from it if the user opts in from Price Tracker Settings.
      if (priceTrackerSyncEnabled) {
      // 1. Find or create vendor in food_vendors
      const vendorName = items[0]?.vendor_name || '';
      let vendorId = null;
      if (vendorName) {
        const { data: existingVendor } = await supabase
          .from('food_vendors')
          .select('id')
          .eq('company_id', companyId)
          .ilike('name', vendorName)
          .maybeSingle();
        if (existingVendor) {
          vendorId = existingVendor.id;
        } else {
          const { data: newVendor } = await supabase
            .from('food_vendors')
            .insert({ company_id: companyId, name: vendorName })
            .select()
            .single();
          vendorId = newVendor?.id || null;
        }
      }

      // 2. Insert into food_invoices
      const invoiceTotal = items.reduce((sum, i) => sum + (i.line_total || 0), 0);
      const invoiceDate = items[0]?.invoice_date || new Date().toISOString().split('T')[0];
      await supabase.from('food_invoices').insert({
        company_id: companyId,
        vendor_id: vendorId,
        date: invoiceDate,
        invoice_number: items[0]?.invoice_number || null,
        total: invoiceTotal,
        source: 'price_tracker',
      });

      // 3. Upsert food_cost_entries — sum line items by category, add to existing day total
      const categoryMap = { 'Proteins': 'proteins', 'Seafood': 'proteins', 'Produce': 'produce', 'Dairy': 'dairy', 'Dry Goods': 'dry_goods', 'Beverages': 'beverages', 'Desserts': 'other', 'Other': 'other' };
      const catTotals = { proteins: 0, produce: 0, dairy: 0, dry_goods: 0, beverages: 0, other: 0 };
      items.forEach(item => {
        const col = categoryMap[item.category] || 'other';
        catTotals[col] += (item.line_total || 0);
      });

      const { data: existingEntry } = await supabase
        .from('food_cost_entries')
        .select('*')
        .eq('company_id', companyId)
        .eq('date', invoiceDate)
        .maybeSingle();

      if (existingEntry) {
        await supabase.from('food_cost_entries').update({
          proteins: (Number(existingEntry.proteins) || 0) + catTotals.proteins,
          produce: (Number(existingEntry.produce) || 0) + catTotals.produce,
          dairy: (Number(existingEntry.dairy) || 0) + catTotals.dairy,
          dry_goods: (Number(existingEntry.dry_goods) || 0) + catTotals.dry_goods,
          beverages: (Number(existingEntry.beverages) || 0) + catTotals.beverages,
          other: (Number(existingEntry.other) || 0) + catTotals.other,
          total: (Number(existingEntry.total) || 0) + invoiceTotal,
          updated_at: new Date().toISOString(),
        }).eq('id', existingEntry.id);
      } else {
        await supabase.from('food_cost_entries').insert({
          company_id: companyId,
          date: invoiceDate,
          ...catTotals,
          total: invoiceTotal,
          source: 'price_tracker',
        });
      }
      } // end if (priceTrackerSyncEnabled)
  }

  // ── Inbound email drain consumer ──
  //
  // Pulls staged inbound_emails rows (status='received') for this company,
  // claims each atomically, downloads its staged attachments from Supabase
  // storage, and runs them through parseFileToItems + saveInvoiceItems
  // exactly as if the operator had uploaded the file manually. The
  // existing realtime subscription on invoices (App.js) fires the
  // live-parse toast once parse_summary lands — same path as manual
  // uploads, no new toast code.
  //
  // Lives inside FoodPriceTracker (not App.js) because parseFileToItems +
  // saveInvoiceItems are React-component closures over companyId and the
  // refresh callbacks. Tradeoff: drain only runs when Price Tracker is
  // mounted. App.js fires a passive "open Price Tracker to parse" toast
  // when an inbound row arrives and the operator is on another page.
  //
  // See src/lib/inboundDrain.js for the helper functions and
  // DESIGN_SYSTEM.md §Email-inbound staged parse for the full convention.
  const drainBusyRef = React.useRef(false);
  const drainQueueRef = React.useRef([]);
  const drainAuthUserIdRef = React.useRef(null);

  // Capture the auth user id once for claim attribution. Doesn't refetch
  // on every drain pass.
  React.useEffect(() => {
    let cancelled = false;
    supabase.auth.getUser().then(({ data }) => {
      if (cancelled) return;
      drainAuthUserIdRef.current = data?.user?.id || null;
    });
    return () => { cancelled = true; };
  }, []);

  const processInboundRow = useCallback(async (row) => {
    const { claimInboundRow, downloadStagedAttachment, markDrainResult } = await import('./lib/inboundDrain');
    // Atomic claim — only one tab wins. If another tab already grabbed
    // it (or the row was deleted), claim returns null and we bail.
    const claimed = await claimInboundRow(row.id, drainAuthUserIdRef.current);
    if (!claimed) return;

    const attachments = Array.isArray(claimed.attachments_json) ? claimed.attachments_json : [];
    const invoiceIds = [];
    const errors = [];

    for (const att of attachments) {
      try {
        const file = await downloadStagedAttachment(att.path, att.name);
        const { invoiceRow, validatedItems } = await parseFileToItems(file);
        await saveInvoiceItems(validatedItems);
        // Stamp the invoice with email provenance so the browser can
        // render the "via email" chip on its row. Done here rather than
        // in saveInvoiceItems to avoid threading a new parameter
        // through that function's existing call sites.
        await supabase.from('invoices').update({
          source_type: 'email_inbound',
          source_email_message_id: claimed.postmark_message_id,
          source_attachment_url: att.path,
          created_by_user_id: drainAuthUserIdRef.current,
        }).eq('id', invoiceRow.id);
        invoiceIds.push(invoiceRow.id);
      } catch (e) {
        console.error('[inboundDrain] parse failed', { inboundId: claimed.id, attachment: att.name, error: e });
        errors.push(`${att.name || 'attachment'}: ${e.message}`);
      }
    }

    if (invoiceIds.length > 0) {
      await markDrainResult(claimed.id, errors.length === 0 ? 'parsed' : 'parsed', { invoiceIds });
    } else {
      await markDrainResult(claimed.id, 'parse_failed', {
        errorMessage: errors.join('; ') || 'No attachments parsed successfully',
      });
    }

    // Refresh local state so any UI bound to invoices/alerts reflects the
    // new rows immediately, without waiting for periodic refresh.
    fetchAlerts();
    fetchReviewItems();
    fetchData();
  }, [parseFileToItems, saveInvoiceItems, fetchAlerts, fetchReviewItems, fetchData]);

  // Drain worker — serialized so we don't fan out N parallel parses (PDF
  // OCR is expensive). Uses a queue + busy flag instead of Promise.all so
  // adding a row to the queue mid-drain is safe.
  const runDrain = useCallback(async () => {
    if (drainBusyRef.current) return;
    drainBusyRef.current = true;
    try {
      while (drainQueueRef.current.length > 0) {
        const next = drainQueueRef.current.shift();
        // eslint-disable-next-line no-await-in-loop
        await processInboundRow(next);
      }
    } finally {
      drainBusyRef.current = false;
    }
  }, [processInboundRow]);

  // Mount-time: pull stale rows (received within 24h that no tab claimed,
  // plus stuck-in-parsing rows past TTL — see inboundDrain.fetchStaleRows).
  // Realtime: subscribe to inbound_emails INSERT+UPDATE for this company,
  // enqueue any row that lands in status='received'.
  React.useEffect(() => {
    if (!companyId) return;
    let cancelled = false;

    (async () => {
      const { fetchStaleRows } = await import('./lib/inboundDrain');
      const stale = await fetchStaleRows(companyId);
      if (cancelled) return;
      drainQueueRef.current.push(...stale);
      runDrain();
    })();

    const channel = supabase
      .channel(`inbound-emails-drain-${companyId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'inbound_emails', filter: `company_id=eq.${companyId}` },
        (payload) => {
          const row = payload.new;
          if (row && row.status === 'received') {
            drainQueueRef.current.push(row);
            runDrain();
          }
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'inbound_emails', filter: `company_id=eq.${companyId}` },
        (payload) => {
          const row = payload.new;
          // Only re-enqueue when status flipped TO 'received' — covers the
          // webhook's two-step path (INSERT row first, then UPDATE with
          // attachments_json) AND stale-TTL recovery resets.
          if (row && row.status === 'received') {
            drainQueueRef.current.push(row);
            runDrain();
          }
        }
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [companyId, runDrain]);

  // Single-file wrapper around saveInvoiceItems — manages modal state + post-save refresh.
  async function saveItems() {
    if (!parsedItems || parsedItems.length === 0) return;
    setSaving(true);
    try {
      await saveInvoiceItems(parsedItems);
      resetUploadModal();
      fetchData();
    } catch (err) {
      setParseError(`Save failed: ${err.message}`);
    }
    setSaving(false);
  }

  // ── Batch upload ──
  // Seeds batchJobs from an initial File[] — rendered immediately so the user sees the queue.
  function startBatchFromFiles(files) {
    const fileList = Array.from(files || []);
    if (fileList.length === 0) return;
    const jobs = fileList.map((file, i) => ({
      id: `${Date.now()}-${i}-${Math.random().toString(36).slice(2, 7)}`,
      file,
      status: 'queued', // queued | parsing | saving | saved | failed | skipped
      itemCount: null,
      error: null,
    }));
    setBatchJobs(jobs);
    setShowUpload(true);
    // Kick off the pool. Accept jobs as an argument so the worker reads from a stable local
    // snapshot (useState updates are async and we can't rely on batchJobs having the new list yet).
    runBatchWorkers(jobs);
  }

  async function runBatchWorkers(initialJobs) {
    if (batchRunning) return;
    setBatchRunning(true);
    // Queue index that workers atomically consume. Using a local variable + mutation gives us
    // a simple producer/consumer without extra refs; Promise.all drains it.
    let cursor = 0;
    const updateJob = (id, patch) => {
      setBatchJobs(prev => prev.map(j => j.id === id ? { ...j, ...patch } : j));
    };
    const worker = async () => {
      while (true) {
        const idx = cursor++;
        if (idx >= initialJobs.length) return;
        const job = initialJobs[idx];
        updateJob(job.id, { status: 'parsing' });
        try {
          const { validatedItems } = await parseFileToItems(job.file, {
            onStep: (msg) => updateJob(job.id, { statusText: msg }),
          });
          updateJob(job.id, { status: 'saving', itemCount: validatedItems.length, statusText: '' });
          await saveInvoiceItems(validatedItems);
          updateJob(job.id, { status: 'saved' });
        } catch (err) {
          const isDup = err.code === 'DUPLICATE_SKIPPED';
          updateJob(job.id, {
            status: isDup ? 'skipped' : 'failed',
            error: err.message || String(err),
            statusText: '',
          });
        }
      }
    };
    const workers = Array.from({ length: BATCH_CONCURRENCY }, worker);
    await Promise.all(workers);
    setBatchRunning(false);
    fetchData();
  }

  // Retry a single failed job. Keeps the others intact so the user doesn't have to re-upload the whole batch.
  async function retryBatchJob(jobId) {
    const job = batchJobs.find(j => j.id === jobId);
    if (!job) return;
    setBatchJobs(prev => prev.map(j => j.id === jobId ? { ...j, status: 'parsing', error: null } : j));
    try {
      const { validatedItems } = await parseFileToItems(job.file);
      setBatchJobs(prev => prev.map(j => j.id === jobId ? { ...j, status: 'saving', itemCount: validatedItems.length } : j));
      await saveInvoiceItems(validatedItems);
      setBatchJobs(prev => prev.map(j => j.id === jobId ? { ...j, status: 'saved' } : j));
      fetchData();
    } catch (err) {
      const isDup = err.code === 'DUPLICATE_SKIPPED';
      setBatchJobs(prev => prev.map(j => j.id === jobId ? { ...j, status: isDup ? 'skipped' : 'failed', error: err.message || String(err) } : j));
    }
  }

  // ── AI Price Check ──

  async function runPriceCheck() {
    setShowPriceCheck(true);
    setPriceCheckLoading(true);
    setPriceCheckResult('');
    try {
      const ninetyDaysAgo = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      const { data: history } = await supabase
        .from('item_price_history')
        .select('food_item_id, unit_price, price_per_lb, invoice_date, vendor_name, unit')
        .eq('company_id', companyId)
        .gte('invoice_date', ninetyDaysAgo)
        .order('invoice_date');

      if (!history || history.length === 0) {
        setPriceCheckResult('Not enough data yet. Upload a few invoices to enable price analysis.');
        setPriceCheckLoading(false);
        return;
      }

      // Build item lookup (includes is_catch_weight flag so we know when case_price is misleading)
      const itemMap = {};
      foodItems.forEach(fi => { itemMap[fi.id] = fi; });

      // Build summary of significant changes
      const byItem = {};
      history.forEach(h => {
        const item = itemMap[h.food_item_id];
        const name = item?.normalized_name || `Item #${h.food_item_id}`;
        if (!byItem[name]) {
          byItem[name] = {
            entries: [],
            is_catch_weight: !!item?.is_catch_weight,
          };
        }
        byItem[name].entries.push({
          price: Number(h.unit_price),
          price_per_lb: h.price_per_lb != null ? Number(h.price_per_lb) : null,
          date: h.invoice_date,
          vendor: h.vendor_name,
        });
      });

      // Only include items with at least 2 data points AND a meaningful change (>= 1%).
      // This prevents Claude from hallucinating insights on flat-priced items.
      // For catch-weight items (meat), use $/lb exclusively since case_price varies with pack weight.
      const MIN_CHANGE_PCT = 1;
      let includedCount = 0;
      let dataSummary = 'PRICE HISTORY DATA (last 90 days). Prices already normalized so that real changes are reflected in the trend percentage. Items with <1% movement were filtered out as noise.\n\n';
      Object.entries(byItem).forEach(([name, { entries, is_catch_weight }]) => {
        if (entries.length < 2) return;

        // Choose the most meaningful price series:
        //   - catch-weight items: $/lb is the real price; case_price drifts with pack weight
        //   - everything else: prefer $/lb when all entries have it (apples-to-apples for subs),
        //     otherwise use case_price
        const allHavePerLb = entries.every(e => e.price_per_lb != null && isFinite(e.price_per_lb));
        const useMetric = is_catch_weight || allHavePerLb ? 'per_lb' : 'case';
        const seriesValues = entries.map(e => useMetric === 'per_lb' ? e.price_per_lb : e.price);
        if (seriesValues.some(v => !isFinite(v) || v <= 0)) return;

        const first = seriesValues[0];
        const last = seriesValues[seriesValues.length - 1];
        const pctChange = ((last - first) / first) * 100;
        if (Math.abs(pctChange) < MIN_CHANGE_PCT) return;

        includedCount++;
        const unitLabel = useMetric === 'per_lb' ? '/lb' : '/case';
        dataSummary += `${name}${is_catch_weight ? ' [catch-weight — priced per lb]' : ''}:\n`;
        entries.forEach((e, i) => {
          const v = seriesValues[i];
          dataSummary += `  ${e.date}: $${v.toFixed(4)}${unitLabel} (${e.vendor || 'unknown vendor'})\n`;
        });
        dataSummary += `  Trend: ${pctChange > 0 ? '+' : ''}${pctChange.toFixed(1)}% (${unitLabel})\n`;

        const rd = regionalData[name];
        if (rd && rd.isActive) {
          dataSummary += `  Regional avg (${rd.contributors} restaurants): $${rd.avg.toFixed(4)}`;
          if (rd.diff != null) dataSummary += ` (you pay ${rd.diff > 0 ? '+' : ''}${rd.diff.toFixed(1)}% vs avg)`;
          dataSummary += '\n';
        }
        dataSummary += '\n';
      });

      if (includedCount === 0) {
        setPriceCheckResult('Your prices have been stable across all tracked items (no item has moved more than 1% in the last 90 days). Nothing urgent to flag.');
        setPriceCheckLoading(false);
        return;
      }

      // Unique weeks
      const weeks = new Set(history.map(h => {
        const d = new Date(h.invoice_date);
        const yearStart = new Date(d.getFullYear(), 0, 1);
        return `${d.getFullYear()}-W${Math.ceil(((d - yearStart) / 86400000 + yearStart.getDay() + 1) / 7)}`;
      }));

      // Get unique invoice count
      const { count } = await supabase
        .from('invoices')
        .select('id', { count: 'exact', head: true })
        .eq('company_id', companyId)
        .gte('invoice_date', ninetyDaysAgo);

      setPriceCheckMeta({ invoices: count || 0, weeks: weeks.size });

      const response = await fetch('/api/parse-invoice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-sonnet-4-5',
          max_tokens: 2048,
          system: PRICE_CHECK_SYSTEM_PROMPT,
          messages: [{ role: 'user', content: dataSummary }],
          _meta: { feature: 'price-check', companyId },
        }),
      });

      const data = await response.json();
      if (!response.ok || data.error || data.type === 'error') {
        setPriceCheckResult('This tool is not available right now. Please check back soon.');
      } else {
        const text = data.content?.[0]?.text;
        setPriceCheckResult(text || 'This tool is not available right now. Please check back soon.');
      }
    } catch (err) {
      setPriceCheckResult(`Error: ${err.message}`);
    }
    setPriceCheckLoading(false);
  }

  // ── Invoice detail ──

  // Change the vendor on an invoice (Price Tracker table) and propagate to all related rows
  // so downstream views (filter dropdown, food_invoices sync, etc.) stay consistent.
  async function updateInvoiceVendor(invoice, newVendorName) {
    const trimmed = (newVendorName || '').trim();
    if (!invoice?.id) return;
    if ((invoice.vendor_name || '') === trimmed) return;

    try {
      // 1. invoices.vendor_name
      await supabase.from('invoices').update({ vendor_name: trimmed }).eq('id', invoice.id);

      // 2. Every line item + price-history row for this invoice carries vendor_name too — update those.
      await Promise.all([
        supabase.from('invoice_line_items').update({ vendor_name: trimmed }).eq('invoice_id', invoice.id),
        supabase.from('item_price_history').update({ vendor_name: trimmed }).eq('invoice_id', invoice.id),
      ]);

      // 3. Update food_items.vendor_name for items whose primary vendor was this invoice's vendor
      //    (only those tied to the old vendor — don't overwrite unrelated items).
      if (invoice.vendor_name) {
        const { data: linkedItems } = await supabase
          .from('invoice_line_items')
          .select('food_item_id')
          .eq('invoice_id', invoice.id);
        const linkedIds = Array.from(new Set((linkedItems || []).map(r => r.food_item_id).filter(Boolean)));
        if (linkedIds.length > 0) {
          await supabase.from('food_items')
            .update({ vendor_name: trimmed })
            .in('id', linkedIds)
            .eq('vendor_name', invoice.vendor_name);
        }
      }

      // 4. If the synced food_invoice exists, point it at the right food_vendors row.
      //    Find or create food_vendors row for the new name.
      let newVendorId = null;
      if (trimmed) {
        const { data: existingV } = await supabase
          .from('food_vendors')
          .select('id')
          .eq('company_id', companyId)
          .ilike('name', trimmed)
          .maybeSingle();
        if (existingV) {
          newVendorId = existingV.id;
        } else {
          const { data: newV } = await supabase
            .from('food_vendors')
            .insert({ company_id: companyId, name: trimmed })
            .select()
            .single();
          newVendorId = newV?.id || null;
        }
      }
      // Update any food_invoices row that matches the old (date, total, price_tracker source).
      if (invoice.invoice_date && invoice.total_amount != null) {
        await supabase.from('food_invoices')
          .update({ vendor_id: newVendorId })
          .eq('company_id', companyId)
          .eq('date', invoice.invoice_date)
          .eq('total', invoice.total_amount)
          .eq('source', 'price_tracker');
      }

      // Auto-cleanup: if the OLD vendor name no longer has any Price Tracker invoices AND its
      // food_vendors row has no remaining food_invoices, delete it so the Food Cost Vendors tab
      // doesn't show orphaned entries.
      const oldVendorName = (invoice.vendor_name || '').trim();
      if (oldVendorName && oldVendorName.toLowerCase() !== trimmed.toLowerCase()) {
        const { count: remainingPtInvoices } = await supabase
          .from('invoices')
          .select('id', { count: 'exact', head: true })
          .eq('company_id', companyId)
          .eq('vendor_name', oldVendorName);
        if ((remainingPtInvoices || 0) === 0) {
          const { data: oldVendorRow } = await supabase
            .from('food_vendors')
            .select('id')
            .eq('company_id', companyId)
            .ilike('name', oldVendorName)
            .maybeSingle();
          if (oldVendorRow?.id) {
            const { count: linkedFoodInvoices } = await supabase
              .from('food_invoices')
              .select('id', { count: 'exact', head: true })
              .eq('vendor_id', oldVendorRow.id);
            if ((linkedFoodInvoices || 0) === 0) {
              await supabase.from('food_vendors').delete().eq('id', oldVendorRow.id);
            }
          }
        }
      }

      setSelectedInvoice(prev => prev ? { ...prev, vendor_name: trimmed } : prev);
      await fetchData();
    } catch (e) {
      alert(`Could not update vendor: ${e.message}`);
    }
  }

  async function viewInvoiceDetail(inv) {
    setSelectedInvoice(inv);
    const { data } = await supabase
      .from('invoice_line_items')
      .select('*')
      .eq('invoice_id', inv.id)
      .order('id');
    setInvoiceLineItems(data || []);
  }

  async function deleteInvoice(inv) {
    if (!window.confirm(`Delete this invoice from ${inv.vendor_name || 'Unknown'}? This will remove all line items and price history entries from this invoice.`)) return;

    // Close modal immediately so UI feels responsive
    setSelectedInvoice(null);
    setInvoiceLineItems([]);

    // Figure out which food_items had price history rows on this invoice so we can
    // recompute their summary fields (current_price, previous_price, price_change_pct, price_per_lb)
    // after the delete. Otherwise those fields stay frozen to the now-deleted entry.
    const { data: affectedRows } = await supabase
      .from('item_price_history')
      .select('food_item_id')
      .eq('invoice_id', inv.id);
    const affectedItemIds = Array.from(new Set((affectedRows || []).map(r => r.food_item_id).filter(Boolean)));

    // Remove price history entries for this invoice
    await supabase.from('item_price_history').delete().eq('invoice_id', inv.id);

    // Remove line items (cascade should handle this, but be explicit)
    await supabase.from('invoice_line_items').delete().eq('invoice_id', inv.id);

    // Recalculate food_items summary for each affected item from remaining history
    if (affectedItemIds.length > 0) {
      // Fetch weight_per_case for each affected item (for price_per_lb recompute)
      const { data: affectedItems } = await supabase
        .from('food_items')
        .select('id, weight_per_case')
        .in('id', affectedItemIds);
      const weightById = {};
      (affectedItems || []).forEach(it => { weightById[it.id] = it.weight_per_case ? Number(it.weight_per_case) : null; });

      // Fetch all remaining history for affected items, ordered ascending by invoice_date
      const { data: remainingHistory } = await supabase
        .from('item_price_history')
        .select('food_item_id, unit_price, price_per_lb, invoice_date')
        .in('food_item_id', affectedItemIds)
        .order('invoice_date', { ascending: true });

      const grouped = {};
      (remainingHistory || []).forEach(h => {
        if (!grouped[h.food_item_id]) grouped[h.food_item_id] = [];
        grouped[h.food_item_id].push(h);
      });

      const updates = affectedItemIds
        .filter(id => grouped[id] && grouped[id].length > 0)
        .map(id => {
          const hist = grouped[id];
          const latest = hist[hist.length - 1];
          const prev = hist.length >= 2 ? hist[hist.length - 2] : null;
          const newCurrent = Number(latest.unit_price);
          const newPrevious = prev ? Number(prev.unit_price) : null;
          const weight = weightById[id];
          const newPricePerLb = latest.price_per_lb != null
            ? Number(latest.price_per_lb)
            : (weight && weight > 0 ? Math.round((newCurrent / weight) * 10000) / 10000 : null);
          // Catch-weight safe: prefer $/lb for change %
          const prevPerLb = prev && prev.price_per_lb != null ? Number(prev.price_per_lb) : null;
          const newChangePct = (newPricePerLb != null && prevPerLb != null && prevPerLb > 0)
            ? Math.round(((newPricePerLb - prevPerLb) / prevPerLb) * 10000) / 100
            : (newPrevious && newPrevious > 0
              ? Math.round(((newCurrent - newPrevious) / newPrevious) * 10000) / 100
              : null);
          return {
            id,
            updates: {
              current_price: newCurrent,
              previous_price: newPrevious,
              price_change_pct: newChangePct,
              price_per_lb: newPricePerLb,
            },
          };
        });

      await Promise.all(updates.map(u =>
        supabase.from('food_items').update(u.updates).eq('id', u.id)
      ));
    }

    // Remove from food_invoices if synced
    if (inv.invoice_number && inv.vendor_name) {
      await supabase.from('food_invoices').delete()
        .eq('company_id', companyId)
        .eq('date', inv.invoice_date)
        .eq('invoice_number', inv.invoice_number)
        .eq('source', 'price_tracker');
    }

    // Recalculate food_cost_entries for that date
    const { data: remaining } = await supabase.from('food_invoices')
      .select('total')
      .eq('company_id', companyId)
      .eq('date', inv.invoice_date);
    const newTotal = (remaining || []).reduce((s, r) => s + Number(r.total || 0), 0);
    const { data: entry } = await supabase.from('food_cost_entries')
      .select('id')
      .eq('company_id', companyId)
      .eq('date', inv.invoice_date)
      .maybeSingle();
    if (entry) {
      if (newTotal > 0) {
        await supabase.from('food_cost_entries').update({ total: newTotal, updated_at: new Date().toISOString() }).eq('id', entry.id);
      } else {
        await supabase.from('food_cost_entries').delete().eq('id', entry.id);
      }
    }

    // Delete the invoice itself
    await supabase.from('invoices').delete().eq('id', inv.id);

    // Clean up orphaned food_items (no remaining price history) — batch approach
    const { data: allItems } = await supabase.from('food_items').select('id').eq('company_id', companyId);
    const { data: activeHistory } = await supabase.from('item_price_history').select('food_item_id').eq('company_id', companyId);
    const activeIds = new Set((activeHistory || []).map(h => h.food_item_id));
    const orphanIds = (allItems || []).filter(i => !activeIds.has(i.id)).map(i => i.id);
    if (orphanIds.length > 0) {
      await supabase.from('food_items').delete().in('id', orphanIds);
    }

    // Clean up storage file if exists
    if (inv.file_url) {
      await supabase.storage.from('invoices').remove([inv.file_url]);
    }

    fetchData();
  }

  // ── Filtered items ──

  // Slice each item's full price history to the selected date range. Powers the items table,
  // sparklines, expanded trend charts, and invoice-card list — keeping the entire page
  // consistent with the Price Impact card's date picker.
  const rangeDays = React.useMemo(() => {
    const { start, end } = priceImpactRange;
    if (!start || !end) return 90;
    const ms = new Date(end + 'T00:00:00').getTime() - new Date(start + 'T00:00:00').getTime();
    return Math.max(1, Math.round(ms / 86400000) + 1); // inclusive of both endpoints
  }, [priceImpactRange]);

  const rangedPriceHistory = React.useMemo(() => {
    const { start, end } = priceImpactRange;
    if (!start || !end) return priceHistory;
    const out = {};
    Object.entries(priceHistory).forEach(([id, entries]) => {
      out[id] = entries.filter(e => e.date && e.date >= start && e.date <= end);
    });
    return out;
  }, [priceHistory, priceImpactRange]);

  // Per-item summary computed from the in-range slice: first vs latest price + change %.
  // Used to override the food_items.current_price/previous_price/price_change_pct columns
  // (which are computed at save-time and aren't range-aware).
  const rangeStats = React.useMemo(() => {
    const stats = {};
    Object.entries(rangedPriceHistory).forEach(([id, entries]) => {
      if (!entries || entries.length === 0) { stats[id] = null; return; }
      const sorted = [...entries].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
      const first = sorted[0];
      const last = sorted[sorted.length - 1];
      const firstPrice = first.price != null ? Number(first.price) : null;
      const lastPrice = last.price != null ? Number(last.price) : null;
      const changePct = (firstPrice && lastPrice && entries.length >= 2)
        ? ((lastPrice - firstPrice) / firstPrice) * 100
        : null;
      stats[id] = {
        currentPrice: lastPrice,
        previousPrice: entries.length >= 2 ? firstPrice : null,
        pricePerLb: last.price_per_lb != null ? Number(last.price_per_lb) : null,
        priceChangePct: changePct,
        entryCount: entries.length,
      };
    });
    return stats;
  }, [rangedPriceHistory]);

  const filteredItems = foodItems
    .filter(item => {
      // Range gate: hide items with no purchases inside the selected date range.
      const rangedEntries = rangedPriceHistory[item.id] || [];
      if (rangedEntries.length === 0) return false;

      if (search && !item.normalized_name.toLowerCase().includes(search.toLowerCase())) return false;
      if (categoryFilter !== 'All' && item.category !== categoryFilter) return false;
      if (vendorFilter !== 'All') {
        // Vendor filter intersects with range: must have an in-range purchase from this vendor.
        const vf = vendorFilter.toLowerCase();
        const historyMatch = rangedEntries.some(h => (h.vendor || '').toLowerCase() === vf);
        if (!historyMatch) return false;
      }
      if (compareOnly && rangedEntries.length < 2) return false;
      if (directionFilter !== 'all') {
        const pct = rangeStats[item.id]?.priceChangePct;
        if (pct == null || !isFinite(pct) || pct === 0) return false;
        if (directionFilter === 'up' && pct <= 0) return false;
        if (directionFilter === 'down' && pct >= 0) return false;
      }
      return true;
    })
    .map(item => {
      const rs = rangeStats[item.id];
      const spendInRange = rangeSpend[item.id] || 0;
      const pct = rs?.priceChangePct;
      return {
        ...item,
        // Override save-time summary fields with in-range equivalents so every column on the
        // page (price, previous, change %, sparkline color) tells the same story as the picker.
        current_price: rs?.currentPrice != null ? rs.currentPrice : item.current_price,
        previous_price: rs?.previousPrice != null ? rs.previousPrice : null,
        price_per_lb: rs?.pricePerLb != null ? rs.pricePerLb : item.price_per_lb,
        price_change_pct: pct != null ? pct : null,
        dollarImpact: (pct != null && spendInRange) ? (pct / 100) * spendInRange : 0,
        totalSpend90d: spendInRange, // kept the field name; column header now reads "$ Spend / range"
      };
    })
    .sort((a, b) => {
      if (sortBy === 'name') return (a.normalized_name || '').localeCompare(b.normalized_name || '');
      if (sortBy === 'code') return (a.product_code || '').localeCompare(b.product_code || '');
      if (sortBy === 'price') return (Number(b.current_price) || 0) - (Number(a.current_price) || 0);
      // Default: % change descending (was $ impact — dropped with the spend columns).
      return Math.abs(Number(b.price_change_pct) || 0) - Math.abs(Number(a.price_change_pct) || 0);
    });

  // ── Vendor Action Items ──

  async function fetchActionItems() {
    const { data } = await supabase
      .from('vendor_action_items')
      .select('*')
      .eq('company_id', companyId)
      .order('estimated_monthly_cost', { ascending: false });
    setActionItems(data || []);
  }

  async function generateActionItems() {
    setActionItemsLoading(true);
    setShowVendorActions(true);

    // Build action items from regional data
    const allItems = [];

    for (const fi of foodItems) {
      const rd = regionalData[fi.normalized_name];
      const spend90 = itemSpend[fi.id] || 0;
      const history = priceHistory[fi.id];
      // Catch-weight: compute priceTrend against $/lb series so pack-weight variance
      // doesn't register as a "market trend". Fixed-pack: $/case is the stable signal.
      // See DESIGN_SYSTEM.md §Three concepts — "Price view continues using
      // is_catch_weight alone, since the question there is 'is $/case unreliable?'"
      const isCW = !!fi.is_catch_weight;
      let priceTrend = 0;
      if (history && history.length >= 2) {
        if (isCW) {
          const perLbSeries = history.filter(h => h.price_per_lb != null).map(h => Number(h.price_per_lb));
          if (perLbSeries.length >= 2) {
            const first = perLbSeries[0], last = perLbSeries[perLbSeries.length - 1];
            priceTrend = first > 0 ? ((last - first) / first) * 100 : 0;
          }
        } else {
          priceTrend = ((Number(history[history.length - 1].price) - Number(history[0].price)) / Number(history[0].price)) * 100;
        }
      }

      // Type 1: Above regional average (vendor issue)
      if (rd && rd.isActive && rd.diff > 2) {
        const monthlyCost = spend90 > 0 ? (rd.diff / 100) * spend90 / 3 : 0;
        allItems.push({
          type: 'above_average',
          item_name: fi.normalized_name,
          your_price: Number(fi.current_price),
          regional_avg: rd.avg,
          pct_above: rd.diff,
          estimated_monthly_cost: Math.round(monthlyCost * 100) / 100,
          spend90,
          price_trend: priceTrend,
        });
      }
      // Type 2: Market trend — price rising >5% but within regional avg (market-wide)
      else if (priceTrend > 5 && (!rd || !rd.isActive || rd.diff <= 2) && spend90 > 0) {
        const monthlyCostIncrease = spend90 > 0 ? (priceTrend / 100) * spend90 / 3 : 0;
        allItems.push({
          type: 'market_trend',
          item_name: fi.normalized_name,
          your_price: Number(fi.current_price),
          regional_avg: rd?.avg || null,
          pct_above: priceTrend,
          estimated_monthly_cost: Math.round(monthlyCostIncrease * 100) / 100,
          spend90,
          price_trend: priceTrend,
        });
      }
    }

    if (allItems.length === 0) {
      // Delete old pending and refresh
      await supabase.from('vendor_action_items').delete().eq('company_id', companyId).eq('status', 'pending');
      await fetchActionItems();
      setActionItemsLoading(false);
      return;
    }

    // Get vendor info from latest invoice line items
    for (const item of allItems) {
      const { data: lineItem } = await supabase
        .from('invoice_line_items')
        .select('vendor_name')
        .eq('company_id', companyId)
        .ilike('normalized_name', item.item_name)
        .order('invoice_date', { ascending: false })
        .limit(1)
        .maybeSingle();
      item.vendor_name = lineItem?.vendor_name || 'Unknown';
    }

    // Build competitor info from regional pool (only for above_average)
    for (const item of allItems) {
      if (item.type === 'above_average') {
        const { data: competitors } = await supabase
          .from('regional_price_pool')
          .select('vendor_name, unit_price')
          .eq('normalized_name', item.item_name)
          .eq('region', companyRegion)
          .order('unit_price', { ascending: true })
          .limit(5);
        const cheapest = (competitors || [])
          .filter(c => c.vendor_name && c.vendor_name !== item.vendor_name)
          .slice(0, 3);
        item.competitor_info = cheapest.length > 0
          ? cheapest.map(c => `${c.vendor_name}: $${Number(c.unit_price).toFixed(2)}`).join(', ')
          : '';
      } else {
        item.competitor_info = '';
      }
    }

    // Delete old pending items and insert fresh ones
    await supabase.from('vendor_action_items').delete().eq('company_id', companyId).eq('status', 'pending');

    const rows = allItems.map(item => ({
      company_id: companyId,
      vendor_name: item.vendor_name,
      item_name: item.item_name,
      your_price: item.your_price,
      regional_avg: item.regional_avg,
      pct_above: item.pct_above,
      estimated_monthly_cost: item.estimated_monthly_cost,
      competitor_info: item.competitor_info,
      status: 'pending',
      type: item.type,
    }));

    if (rows.length > 0) {
      await supabase.from('vendor_action_items').insert(rows);
    }

    await fetchActionItems();
    setActionItemsLoading(false);
  }

  async function generateEmailDraft(vendorName, items) {
    setGeneratingEmail(vendorName);
    try {
      const itemLines = items.map(i =>
        `- ${i.item_name}: currently $${Number(i.your_price).toFixed(2)}, regional average is $${Number(i.regional_avg).toFixed(2)} (+${Number(i.pct_above).toFixed(1)}%)${i.competitor_info ? ` — comparable pricing: ${i.competitor_info}` : ''}`
      ).join('\n');

      const response = await fetch('/api/parse-invoice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-sonnet-4-5',
          max_tokens: 1024,
          system: 'You write professional, concise business emails for restaurant managers. Be direct but polite. Never be confrontational. Focus on partnership and finding mutual value.',
          messages: [{ role: 'user', content: `Write a professional email to ${vendorName} requesting a price review on the following items. Mention we value the relationship but have noticed our pricing is above market average and want to discuss options.\n\n${itemLines}\n\nKeep it under 150 words. Don't include subject line — just the body. Sign off as the restaurant manager.` }],
          _meta: { feature: 'vendor-email', companyId },
        }),
      });
      const data = await response.json();
      if (!response.ok || data?.type === 'error' || data?.error) {
        setGeneratingEmail(null);
        alert('This tool is not available right now. Please check back soon.');
        return;
      }
      const text = data.content?.[0]?.text || 'This tool is not available right now. Please check back soon.';
      setEmailDraft({ vendor: vendorName, body: text });
    } catch (err) {
      setEmailDraft({ vendor: vendorName, body: `Error generating email: ${err.message}` });
    }
    setGeneratingEmail(null);
  }

  async function markContacted(itemId) {
    await supabase.from('vendor_action_items').update({
      status: 'contacted',
      contacted_date: new Date().toISOString().split('T')[0],
      updated_at: new Date().toISOString(),
    }).eq('id', itemId);
    fetchActionItems();
  }

  async function dismissItem(itemId) {
    await supabase.from('vendor_action_items').update({
      status: 'dismissed',
      updated_at: new Date().toISOString(),
    }).eq('id', itemId);
    fetchActionItems();
  }

  async function resolveItem(itemId) {
    await supabase.from('vendor_action_items').update({
      status: 'resolved',
      outcome: outcomeForm.outcome,
      outcome_notes: outcomeForm.notes,
      resolved_date: new Date().toISOString().split('T')[0],
      updated_at: new Date().toISOString(),
    }).eq('id', itemId);
    setShowOutcomeModal(null);
    setOutcomeForm({ outcome: '', notes: '' });
    fetchActionItems();
  }

  // Load action items on mount
  useEffect(() => {
    if (companyId) fetchActionItems();
  }, [companyId]);

  // ── Mobile detection ──
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // ── Main render ──

  return (
    <div className="flex-1 overflow-y-auto p-6 bg-gray-50" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>Food Price Tracker</h1>
          <p className="text-sm text-gray-400 mt-0.5">Track ingredient prices across invoices</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={generateActionItems}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 relative"
            style={{ borderRadius: 10 }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/></svg>
            Vendor Actions
            {actionItems.filter(a => a.status === 'pending').length > 0 && (
              <span className="ml-1 w-5 h-5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center">{actionItems.filter(a => a.status === 'pending').length}</span>
            )}
          </button>
          <button
            onClick={runPriceCheck}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
            style={{ borderRadius: 10 }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/></svg>
            AI Price Check
          </button>
          <button
            onClick={() => setShowSettings(true)}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
            style={{ borderRadius: 10 }}
            title="Price Tracker settings"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/><path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
            Settings
          </button>
          <button
            onClick={() => setShowUpload(true)}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg text-white"
            style={{ background: 'var(--text-primary)', color: 'var(--bg-card)', borderRadius: 10 }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 4v16m8-8H4"/></svg>
            Upload Invoice
          </button>
        </div>
      </div>

      {/* Data Review — parse-guardrail flag queue. Renders above Vendor Watch
          per DESIGN_SYSTEM.md §Data Review queue. Section collapses to nothing
          when zero items flagged. */}
      <DataReview
        companyId={companyId}
        items={reviewItems}
        onResolved={() => { fetchReviewItems(); fetchData(); }}
        onEdit={(item) => {
          // Open the items list, expand the row, and scroll into view. Reusing
          // the existing inline expanded-row edit affordance — that surface
          // already lets the user fix pack/price inline.
          setExpandedItem(item.id);
          setTimeout(() => {
            document.querySelector(`[data-food-item-id="${item.id}"]`)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }, 50);
        }}
      />

      {/* Vendor Watch — durable shrinkflation alerts. Section collapses to nothing
          when zero alerts exist (no "all good" empty state — adds noise for the
          common case). */}
      <VendorWatch
        alerts={alerts}
        itemsById={itemsById}
        onViewItem={(food_item_id) => {
          setExpandedItem(food_item_id);
          // Scroll into view if on the items table
          setTimeout(() => {
            document.querySelector(`[data-food-item-id="${food_item_id}"]`)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }, 50);
        }}
        onAlertChange={fetchAlerts}
        onRunCheck={handleDryRun}
      />

      {/* Price Impact Card — always visible, 3-column layout. Click anywhere to expand for more
          contributors; pencil icon enters date-edit mode; "see →" on a contributor opens the detail modal. */}
      {(() => {
        const fmtDate = (iso) => {
          if (!iso) return '';
          const [y, m, d] = iso.split('-');
          return new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        };
        const net = priceImpact.net;
        const isSaving = net < -0.5;
        const isCost = net > 0.5;
        const netColor = isCost ? 'var(--text-critical)' : (isSaving ? 'var(--text-success)' : 'var(--text-primary)');
        const sign = net > 0 ? '+' : (net < 0 ? '-' : '');
        const absNet = Math.abs(net);
        const ups = (priceImpact.contributors || []).filter(c => c.delta > 0).sort((a, b) => b.delta - a.delta);
        const downs = (priceImpact.contributors || []).filter(c => c.delta < 0).sort((a, b) => a.delta - b.delta);
        const topN = priceImpactExpanded ? 5 : 3;
        const upsToShow = priceImpactExpanded && showAllUp ? ups : ups.slice(0, topN);
        const downsToShow = priceImpactExpanded && showAllDown ? downs : downs.slice(0, topN);

        const Contributor = ({ c, color }) => {
          const fmtPrice = (v) => v != null && isFinite(v) ? `$${Number(v).toFixed(2)}` : '—';
          const deltaSign = c.delta >= 0 ? '+' : '-';
          // Catch-weight items: show $/lb as the primary delta (honest signal) with the
          // aggregate $/case amount as smaller secondary context. Fixed-pack: single
          // $/case delta, as before. Decision documented per DESIGN_SYSTEM.md §Three concepts.
          const perLbDelta = (c.is_catch_weight && c.baseline_per_lb != null && c.current_per_lb != null)
            ? Number(c.current_per_lb) - Number(c.baseline_per_lb)
            : null;
          return (
            <div className="flex items-start justify-between gap-3 py-1.5 border-b border-gray-50 last:border-b-0">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-gray-900 truncate flex items-center gap-1">
                  <span style={{ color }}>{c.delta >= 0 ? '▲' : '▼'}</span>
                  <span className="truncate">{c.name}</span>
                  {c.is_catch_weight && <span className="text-gray-300 font-normal" style={{ fontSize: 9 }}>(cw)</span>}
                </div>
                {priceImpactExpanded && (
                  <div className="text-xs text-gray-400 mt-0.5">
                    {fmtPrice(c.baseline_price)} → {fmtPrice(c.current_price)}{c.unit_label}
                    {c.qty > 0 && <span> · {Number(c.qty).toLocaleString('en-US', { maximumFractionDigits: 1 })} cs</span>}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="text-right">
                  {perLbDelta != null ? (
                    <>
                      <div className="text-sm font-bold" style={{ color }}>
                        {perLbDelta >= 0 ? '+' : '-'}${Math.abs(perLbDelta).toFixed(2)}/lb
                      </div>
                      <div className="text-gray-400" style={{ fontSize: 10 }}>
                        {deltaSign}${Math.abs(c.delta).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                      </div>
                    </>
                  ) : (
                    <span className="text-sm font-bold" style={{ color }}>
                      {deltaSign}${Math.abs(c.delta).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                    </span>
                  )}
                </div>
                {priceImpactExpanded && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setDetailModalItemId(c.food_item_id); }}
                    className="text-xs text-gray-400 hover:text-gray-900 underline whitespace-nowrap"
                  >
                    see →
                  </button>
                )}
              </div>
            </div>
          );
        };

        return (
          <div
            className="mb-4 bg-white rounded-2xl border border-gray-200 p-5 cursor-pointer hover:border-gray-300 transition-colors"
            style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
            onClick={() => { if (!priceImpactEditing) setPriceImpactExpanded(v => !v); }}
          >
            {/* Top row: date range chip (top-right, with pencil), collapse caret */}
            <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
              {/* Label relabeled from "Price Impact" to "Price Change Net" — the math is
                  already $/lb-aware for catch-weight items (computePriceImpact uses
                  ($/lb − baseline $/lb) × total_weight on the CW subset), so this is a
                  price-change metric, not a spend metric. Per DESIGN_SYSTEM.md §Three concepts. */}
              <div className="text-xs font-medium text-gray-400 uppercase tracking-wide">Price Change Net</div>
              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                {priceImpactEditing ? (
                  <div className="flex items-center gap-2 flex-wrap">
                    <input
                      type="date" value={priceImpactRange.start} max={priceImpactRange.end}
                      onChange={(e) => setPriceImpactRange(r => ({ ...r, start: e.target.value }))}
                      className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-gray-900"
                      style={{ borderRadius: 8 }}
                    />
                    <span className="text-gray-400">→</span>
                    <input
                      type="date" value={priceImpactRange.end} min={priceImpactRange.start}
                      onChange={(e) => setPriceImpactRange(r => ({ ...r, end: e.target.value }))}
                      className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-gray-900"
                      style={{ borderRadius: 8 }}
                    />
                    <button onClick={() => {
                      const end = new Date().toISOString().split('T')[0];
                      const start = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
                      setPriceImpactRange({ start, end });
                    }} className="px-2 py-1 text-xs text-gray-500 hover:text-gray-900 underline">30d</button>
                    <button onClick={() => {
                      const end = new Date().toISOString().split('T')[0];
                      const start = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
                      setPriceImpactRange({ start, end });
                    }} className="px-2 py-1 text-xs text-gray-500 hover:text-gray-900 underline">90d</button>
                    <button onClick={() => {
                      const end = new Date().toISOString().split('T')[0];
                      const start = `${new Date().getFullYear()}-01-01`;
                      setPriceImpactRange({ start, end });
                    }} className="px-2 py-1 text-xs text-gray-500 hover:text-gray-900 underline">YTD</button>
                    <button onClick={() => setPriceImpactEditing(false)} className="px-3 py-1.5 text-xs font-medium rounded-lg" style={{ background: 'var(--text-primary)', color: 'var(--bg-card)' }}>Done</button>
                  </div>
                ) : (
                  <button
                    onClick={() => setPriceImpactEditing(true)}
                    className="flex items-center gap-1.5 text-xs text-gray-600 hover:text-gray-900 border border-gray-200 rounded-lg px-2.5 py-1.5"
                    style={{ borderRadius: 8 }}
                    title="Edit date range"
                  >
                    <span>{fmtDate(priceImpactRange.start)} → {fmtDate(priceImpactRange.end)}</span>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                  </button>
                )}
              </div>
            </div>

            {/* Body grid: hero | top increases | top decreases */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div>
                <div className="flex items-baseline gap-2 flex-wrap">
                  {priceImpact.loading ? (
                    <div className="text-4xl font-bold text-gray-300">—</div>
                  ) : (
                    <div className="text-4xl font-bold" style={{ color: netColor }}>
                      {sign}${absNet.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                    </div>
                  )}
                </div>
                {!priceImpact.loading && (priceImpact.itemsUp > 0 || priceImpact.itemsDown > 0) && (
                  <div className="text-xs text-gray-500 mt-1">
                    <span className="text-red-600 font-medium">{priceImpact.itemsUp}</span> up
                    {' · '}
                    <span className="text-green-600 font-medium">{priceImpact.itemsDown}</span> down
                  </div>
                )}
                {!priceImpact.loading && !priceImpact.coveredLines && (
                  <div className="text-xs text-gray-400 mt-1">No comparable purchases in this range</div>
                )}
                {!priceImpact.loading && priceImpact.coveredLines > 0 && (
                  <div className="text-xs text-gray-400 mt-2 leading-relaxed">
                    {isCost ? 'Price increases have cost you this much ' : (isSaving ? 'You saved this much from price decreases ' : 'No meaningful cost change ')}
                    vs baseline prices before the range.
                  </div>
                )}
                <div className="text-xs text-gray-400 mt-3 hidden md:block">
                  {priceImpactExpanded ? 'click anywhere to collapse ↑' : 'click anywhere to see breakdown ↓'}
                </div>
              </div>

              <div>
                <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">
                  Top increases
                </div>
                {ups.length === 0 && !priceImpact.loading ? (
                  <div className="text-xs text-gray-400">None in this range</div>
                ) : (
                  <div>
                    {upsToShow.map(c => <Contributor key={c.food_item_id} c={c} color="#DC2626" />)}
                    {priceImpactExpanded && ups.length > topN && !showAllUp && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setShowAllUp(true); }}
                        className="mt-2 text-xs text-gray-500 hover:text-gray-900 underline"
                      >
                        Show all {ups.length}
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div>
                <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">
                  Top decreases
                </div>
                {downs.length === 0 && !priceImpact.loading ? (
                  <div className="text-xs text-gray-400">None in this range</div>
                ) : (
                  <div>
                    {downsToShow.map(c => <Contributor key={c.food_item_id} c={c} color="#2D8F5E" />)}
                    {priceImpactExpanded && downs.length > topN && !showAllDown && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setShowAllDown(true); }}
                        className="mt-2 text-xs text-gray-500 hover:text-gray-900 underline"
                      >
                        Show all {downs.length}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-2xl border border-gray-200 p-4" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Total Items</div>
          <div className="text-2xl font-bold text-gray-900">{summary.total}</div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-4" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Invoices This Month</div>
          <div className="text-2xl font-bold text-gray-900">{summary.invoicesThisMonth}</div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-4" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Costliest Increase</div>
          {summary.biggestImpact ? (
            <div>
              <div className="text-sm font-medium text-gray-900 truncate">{summary.biggestImpact.normalized_name}</div>
              <div className="flex items-center gap-2 mt-0.5">
                <PriceChangePill pct={summary.biggestImpact.price_change_pct} />
                <span className="text-xs font-semibold text-red-600">+${Math.abs(summary.biggestImpact.dollarImpact).toFixed(0)}/90d</span>
              </div>
            </div>
          ) : <div className="text-sm text-gray-400">No data yet</div>}
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-4" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Biggest Savings</div>
          {summary.biggestDrop ? (
            <div>
              <div className="text-sm font-medium text-gray-900 truncate">{summary.biggestDrop.normalized_name}</div>
              <div className="flex items-center gap-2 mt-0.5">
                <PriceChangePill pct={summary.biggestDrop.price_change_pct} />
                <span className="text-xs font-semibold text-green-600">-${Math.abs(summary.biggestDrop.dollarImpact).toFixed(0)}/90d</span>
              </div>
            </div>
          ) : <div className="text-sm text-gray-400">No data yet</div>}
        </div>
      </div>

      {/* Vendor Action Items Panel */}
      {showVendorActions && (
        <div className="mb-6 bg-white rounded-2xl border border-gray-200" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#111827" strokeWidth="2"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/></svg>
              <h2 className="text-sm font-semibold text-gray-900">Vendor Conversations</h2>
              <span className="text-xs text-gray-400">Items where you're overpaying vs {companyRegion} avg</span>
            </div>
            <button onClick={() => setShowVendorActions(false)} className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
            </button>
          </div>

          {actionItemsLoading ? (
            <div className="p-8 text-center">
              <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin mx-auto mb-3" />
              <p className="text-sm text-gray-500">Analyzing vendors...</p>
            </div>
          ) : (() => {
            const pending = actionItems.filter(a => a.status === 'pending' || a.status === 'contacted');
            const resolved = actionItems.filter(a => a.status === 'resolved' || a.status === 'dismissed');

            // Split by type
            const aboveAvg = pending.filter(a => a.type !== 'market_trend');
            const marketTrend = pending.filter(a => a.type === 'market_trend');

            // Group above-avg by vendor
            const byVendor = {};
            aboveAvg.forEach(a => {
              if (!byVendor[a.vendor_name]) byVendor[a.vendor_name] = [];
              byVendor[a.vendor_name].push(a);
            });
            const vendors = Object.entries(byVendor).sort((a, b) => {
              const aCost = a[1].reduce((s, i) => s + Number(i.estimated_monthly_cost || 0), 0);
              const bCost = b[1].reduce((s, i) => s + Number(i.estimated_monthly_cost || 0), 0);
              return bCost - aCost;
            });

            if (pending.length === 0 && resolved.length === 0) {
              return (
                <div className="p-8 text-center">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2D8F5E" strokeWidth="2.5"><path d="M5 13l4 4L19 7"/></svg>
                  </div>
                  <p className="text-sm font-medium text-gray-900">All clear</p>
                  <p className="text-xs text-gray-400 mt-1">No pricing issues or market trends to flag</p>
                </div>
              );
            }

            const totalAboveAvgSavings = aboveAvg.reduce((s, a) => s + Number(a.estimated_monthly_cost || 0), 0);
            const totalTrendCost = marketTrend.reduce((s, a) => s + Number(a.estimated_monthly_cost || 0), 0);

            return (
              <div className="divide-y divide-gray-50">
                {/* Total potential savings banner */}
                {(totalAboveAvgSavings > 0 || totalTrendCost > 0) && (
                  <div className="px-5 py-3 flex items-center gap-4 flex-wrap" style={{ background: 'linear-gradient(135deg, #FEF2F2, #FFFBEB)' }}>
                    {totalAboveAvgSavings > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
                        <span className="text-sm text-red-700">Vendor savings: <strong>${totalAboveAvgSavings.toFixed(0)}/mo</strong></span>
                      </div>
                    )}
                    {totalTrendCost > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                        <span className="text-sm text-amber-700">Rising costs: <strong>${totalTrendCost.toFixed(0)}/mo</strong></span>
                      </div>
                    )}
                  </div>
                )}

                {/* ── ABOVE REGIONAL AVERAGE (Red) ── */}
                {aboveAvg.length > 0 && (
                  <div className="px-5 py-3 border-b border-gray-100">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
                      <span className="text-xs font-bold text-red-700 uppercase tracking-wide">Above Regional Average</span>
                      <span className="text-xs text-gray-400">— negotiate with vendor</span>
                    </div>
                  </div>
                )}

                {/* Vendor groups (above avg) */}
                {vendors.map(([vendor, items]) => {
                  const vendorTotal = items.reduce((s, i) => s + Number(i.estimated_monthly_cost || 0), 0);
                  return (
                    <div key={vendor} className="px-5 py-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center text-xs font-bold text-red-600">
                            {vendor.split(' ').map(w => w[0]).join('').slice(0, 2)}
                          </div>
                          <div>
                            <h3 className="text-sm font-semibold text-gray-900">{vendor}</h3>
                            <span className="text-xs text-gray-400">{items.length} item{items.length > 1 ? 's' : ''} to discuss — ${vendorTotal.toFixed(0)}/mo potential savings</span>
                          </div>
                        </div>
                        <button
                          onClick={() => generateEmailDraft(vendor, items)}
                          disabled={generatingEmail === vendor}
                          className="flex items-center gap-1 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50 disabled:opacity-50"
                        >
                          {generatingEmail === vendor ? (
                            <><div className="w-3 h-3 border border-gray-300 border-t-gray-900 rounded-full animate-spin" /> Drafting...</>
                          ) : (
                            <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg> Draft Email</>
                          )}
                        </button>
                      </div>

                      <div className="space-y-2">
                        {items.map(item => (
                          <div key={item.id} className="flex items-center justify-between bg-red-50/50 rounded-xl px-4 py-2.5">
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-gray-900">{item.item_name}</div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                You: ${Number(item.your_price).toFixed(2)} vs avg: ${Number(item.regional_avg).toFixed(2)}
                                {item.competitor_info && <span className="text-gray-400"> — {item.competitor_info}</span>}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                              <span className="text-xs font-bold text-white bg-red-500 px-2 py-0.5 rounded-lg">+{Number(item.pct_above).toFixed(1)}%</span>
                              {item.status === 'pending' && (
                                <>
                                  <button onClick={() => markContacted(item.id)} className="text-xs hover:underline font-medium" style={{ color: 'var(--accent-primary)', background: 'transparent', border: 'none', cursor: 'pointer' }}>Mark Contacted</button>
                                  <button onClick={() => dismissItem(item.id)} className="text-xs text-gray-400 hover:text-gray-600">Dismiss</button>
                                </>
                              )}
                              {item.status === 'contacted' && (
                                <button onClick={() => { setShowOutcomeModal(item); setOutcomeForm({ outcome: '', notes: '' }); }} className="text-xs font-medium text-amber-600 border border-amber-200 bg-amber-50 rounded-lg px-2 py-0.5">
                                  Log Outcome
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}

                {/* ── RISING MARKET PRICES (Orange) ── */}
                {marketTrend.length > 0 && (
                  <div className="px-5 pt-4 pb-2">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                      <span className="text-xs font-bold text-amber-700 uppercase tracking-wide">Rising Market Prices</span>
                      <span className="text-xs text-gray-400">— industry-wide, consider adapting</span>
                    </div>
                    <div className="space-y-2 pb-2">
                      {marketTrend.map(item => (
                        <div key={item.id} className="flex items-center justify-between bg-amber-50/50 rounded-xl px-4 py-2.5">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">{item.item_name}</span>
                              <span className="text-xs text-gray-400">via {item.vendor_name}</span>
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              Price up {Number(item.pct_above).toFixed(1)}% over 90 days
                              {item.regional_avg && <span> — matches regional avg (${Number(item.regional_avg).toFixed(2)})</span>}
                              <span className="text-amber-600 font-medium"> — +${Number(item.estimated_monthly_cost).toFixed(0)}/mo cost increase</span>
                            </div>
                            <div className="text-xs text-amber-600 mt-1">
                              Consider: menu price adjustment, alternative ingredients, or locking in current rate
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                            <span className="text-xs font-bold text-white bg-amber-500 px-2 py-0.5 rounded-lg">+{Number(item.pct_above).toFixed(1)}%</span>
                            {item.status === 'pending' && (
                              <>
                                <button onClick={() => markContacted(item.id)} className="text-xs hover:underline font-medium" style={{ color: 'var(--accent-primary)', background: 'transparent', border: 'none', cursor: 'pointer' }}>Addressed</button>
                                <button onClick={() => dismissItem(item.id)} className="text-xs text-gray-400 hover:text-gray-600">Dismiss</button>
                              </>
                            )}
                            {item.status === 'contacted' && (
                              <button onClick={() => { setShowOutcomeModal(item); setOutcomeForm({ outcome: '', notes: '' }); }} className="text-xs font-medium text-amber-600 border border-amber-200 bg-amber-50 rounded-lg px-2 py-0.5">
                                Log Outcome
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Resolved items */}
                {resolved.length > 0 && (
                  <div className="px-5 py-3">
                    <details>
                      <summary className="text-xs font-medium text-gray-400 cursor-pointer hover:text-gray-600">{resolved.length} resolved/dismissed item{resolved.length > 1 ? 's' : ''}</summary>
                      <div className="mt-2 space-y-1.5">
                        {resolved.map(item => (
                          <div key={item.id} className="flex items-center justify-between text-xs text-gray-400 bg-gray-50 rounded-lg px-3 py-2">
                            <span>{item.item_name} — {item.vendor_name}</span>
                            <div className="flex items-center gap-2">
                              {item.outcome && <span className="text-gray-500">{item.outcome.replace(/_/g, ' ')}</span>}
                              <span className={`px-1.5 py-0.5 rounded text-xs ${item.status === 'resolved' ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-400'}`}>{item.status}</span>
                              {item.resolved_date && <span>{item.resolved_date}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </details>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* Email Draft Modal */}
      {emailDraft && (
        <div className="fixed inset-0 z-50 flex items-center justify-center modal-backdrop" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="bg-white rounded-2xl w-full max-w-lg modal-content" style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">Email Draft — {emailDraft.vendor}</h2>
                <p className="text-xs text-gray-400 mt-0.5">Review and copy to send</p>
              </div>
              <button onClick={() => setEmailDraft(null)} className="text-gray-400 hover:text-gray-600 text-lg">&times;</button>
            </div>
            <div className="p-5">
              <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                {emailDraft.body}
              </div>
              <div className="flex items-center gap-3 mt-4">
                <button
                  onClick={() => { navigator.clipboard.writeText(emailDraft.body); }}
                  className="flex-1 py-2.5 text-sm font-medium text-white rounded-lg"
                  style={{ background: 'var(--text-primary)', color: 'var(--bg-card)', borderRadius: 10 }}
                >
                  Copy to Clipboard
                </button>
                <button onClick={() => setEmailDraft(null)} className="px-4 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50" style={{ borderRadius: 10 }}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Outcome Modal */}
      {showOutcomeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center modal-backdrop" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="bg-white rounded-2xl w-full max-w-sm modal-content" style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            <div className="p-5 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-900">Log Outcome — {showOutcomeModal.item_name}</h2>
              <p className="text-xs text-gray-400 mt-0.5">{showOutcomeModal.vendor_name}</p>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">What happened?</label>
                <select
                  value={outcomeForm.outcome}
                  onChange={e => setOutcomeForm(p => ({ ...p, outcome: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-gray-900"
                  style={{ borderRadius: 8 }}
                >
                  <option value="">Select outcome...</option>
                  <option value="matched_price">Matched price</option>
                  <option value="partial_discount">Offered partial discount</option>
                  <option value="no_change">No change</option>
                  <option value="switched_vendor">Switching vendor</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Notes (optional)</label>
                <textarea
                  value={outcomeForm.notes}
                  onChange={e => setOutcomeForm(p => ({ ...p, notes: e.target.value }))}
                  placeholder="e.g. Rep said new pricing starts next week..."
                  rows={3}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-gray-900 resize-none"
                  style={{ borderRadius: 8 }}
                />
              </div>
              <div className="flex items-center gap-3 pt-1">
                <button
                  onClick={() => resolveItem(showOutcomeModal.id)}
                  disabled={!outcomeForm.outcome}
                  className="flex-1 py-2.5 text-sm font-medium text-white rounded-lg disabled:opacity-40"
                  style={{ background: 'var(--text-primary)', color: 'var(--bg-card)', borderRadius: 10 }}
                >
                  Save Outcome
                </button>
                <button onClick={() => setShowOutcomeModal(null)} className="px-4 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50" style={{ borderRadius: 10 }}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main content: Table + Sidebar */}
      <div className={`flex gap-6 ${isMobile ? 'flex-col' : ''}`}>
        {/* Items table */}
        <div className={isMobile ? 'w-full' : 'flex-1 min-w-0'}>
          {/* Sidebar toggle when collapsed */}
          {!sidebarOpen && !isMobile && (
            <div className="flex justify-end mb-2">
              <button
                onClick={() => setSidebarOpen(true)}
                className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 px-2 py-1 rounded-lg hover:bg-gray-100"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                Show Invoices
              </button>
            </div>
          )}
          {/* View-mode toggle + spend-link. Price view is the default; Volume flips the
              columns to quantity metrics on the same list. Spend lives in Operations > Food
              Costs per DESIGN_SYSTEM.md §Three concepts. */}
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <SegmentedPills
              value={viewMode}
              onChange={setViewMode}
              options={[
                { key: 'price',  label: 'Price' },
                { key: 'volume', label: 'Volume' },
              ]}
              size="sm"
            />
            <a
              href="#food-costs"
              onClick={(e) => { e.preventDefault(); navigate('operations', 'food'); }}
              className="text-xs"
              style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}
              onMouseEnter={e => { e.currentTarget.style.color = 'var(--accent-primary)'; e.currentTarget.style.textDecoration = 'underline'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.textDecoration = 'none'; }}
              title="Spend lives in Operations > Food Costs"
            >
              View spend in Food Costs →
            </a>
          </div>
          <div className="bg-white rounded-2xl border border-gray-200" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            {/* Search + filter bar — wraps on narrow screens so controls don't overflow the card. */}
            <div className="p-4 border-b border-gray-100 flex items-center gap-3 flex-wrap">
              <div className="relative flex-1" style={{ minWidth: 200 }}>
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                <input
                  type="text"
                  placeholder="Search items..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-900"
                  style={{ borderRadius: 8 }}
                />
              </div>
              <select
                value={categoryFilter}
                onChange={e => setCategoryFilter(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-600 focus:outline-none focus:border-gray-900"
                style={{ borderRadius: 8 }}
              >
                <option value="All">All Categories</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <select
                value={vendorFilter}
                onChange={e => setVendorFilter(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-600 focus:outline-none focus:border-gray-900"
                style={{ borderRadius: 8 }}
              >
                <option value="All">All Vendors</option>
                {knownVendors.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-600 focus:outline-none focus:border-gray-900"
                style={{ borderRadius: 8 }}
              >
                {/* $ Impact sort removed with the $ Impact column (see DESIGN_SYSTEM.md
                    §Three concepts — spend metrics moved to Operations > Food Costs). */}
                <option value="change">Sort: % Change</option>
                <option value="name">Sort: Name</option>
                <option value="code">Sort: Item #</option>
                <option value="price">Sort: Price</option>
              </select>
              <button
                onClick={() => setCompareOnly(v => !v)}
                className="rounded-lg px-3 py-2 text-sm focus:outline-none"
                style={{
                  borderRadius: 8,
                  border: '1px solid',
                  borderColor: compareOnly ? 'var(--text-primary)' : 'var(--border)',
                  background: compareOnly ? 'var(--text-primary)' : 'var(--bg-card)',
                  color: compareOnly ? 'var(--bg-card)' : 'var(--text-secondary)',
                }}
                title="Show only items with 2+ price history entries"
              >
                {compareOnly ? '✓ Compare Only' : 'Compare Only'}
              </button>
              <div className="inline-flex rounded-lg overflow-hidden" style={{ borderRadius: 8, border: '1px solid var(--border)' }}>
                <button
                  onClick={() => setDirectionFilter('all')}
                  className="px-3 py-2 text-sm focus:outline-none"
                  style={{
                    background: directionFilter === 'all' ? 'var(--text-primary)' : 'var(--bg-card)',
                    color: directionFilter === 'all' ? 'var(--bg-card)' : 'var(--text-secondary)',
                  }}
                  title="Show all items"
                >
                  All
                </button>
                <button
                  onClick={() => setDirectionFilter('up')}
                  className="px-3 py-2 text-sm focus:outline-none"
                  style={{
                    borderLeft: '1px solid var(--border)',
                    background: directionFilter === 'up' ? 'var(--text-critical)' : 'var(--bg-card)',
                    color: directionFilter === 'up' ? '#FFFFFF' : 'var(--text-critical)',
                  }}
                  title="Show only items whose price went up"
                >
                  ▲ Up
                </button>
                <button
                  onClick={() => setDirectionFilter('down')}
                  className="px-3 py-2 text-sm focus:outline-none"
                  style={{
                    borderLeft: '1px solid var(--border)',
                    background: directionFilter === 'down' ? 'var(--text-success)' : 'var(--bg-card)',
                    color: directionFilter === 'down' ? '#FFFFFF' : 'var(--text-success)',
                  }}
                  title="Show only items whose price went down"
                >
                  ▼ Down
                </button>
              </div>
            </div>

            {/* Empty state */}
            {!loading && foodItems.length === 0 && (
              <div className="py-16 text-center">
                <svg className="mx-auto mb-4 text-gray-300" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                <h3 className="text-base font-medium text-gray-900 mb-1">No invoices yet</h3>
                <p className="text-sm text-gray-400 mb-4">Upload your first invoice to start tracking prices</p>
                <button
                  onClick={() => setShowUpload(true)}
                  className="px-4 py-2 text-sm font-medium text-white rounded-lg"
                  style={{ background: 'var(--text-primary)', color: 'var(--bg-card)', borderRadius: 10 }}
                >
                  Upload Invoice
                </button>
              </div>
            )}

            {/* Table (desktop). Per DESIGN_SYSTEM.md §Three concepts, Price Tracker shows
                Price metrics only. $ Spend / range and $ Impact (both spend metrics) moved
                to Operations > Food Costs. Link provided above the table. Column set:
                Item | Category | Price (primary) | Previous | Change | Trend. */}
            {!isMobile && viewMode === 'volume' && (loading || filteredItems.length > 0) && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wide">Item</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wide">Category</th>
                      <th className="text-right px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wide">This Period</th>
                      <th className="text-right px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wide">vs Prior</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wide">Trend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(loading || volumeLoading) ? (
                      [...Array(5)].map((_, i) => <ShimmerRow key={i} />)
                    ) : (
                      filteredItems.map(item => {
                        const vol = volumeData[item.id] || { curr: { qty: 0, lbs: 0 }, prior: { qty: 0, lbs: 0 }, sparkline: [] };
                        const lines = rangedPriceHistory[item.id] || [];
                        const useWeight = hasWeightData(item, lines);
                        return (
                          <VolumeRow
                            key={item.id}
                            item={item}
                            vol={vol}
                            useWeight={useWeight}
                            alert={activeAlertByItemId[item.id]}
                            needsReviewItem={needsReviewByItemId[item.id]}
                            onOpenDrawer={() => setVolumeDrawer({ item, useWeight })}
                            onAlertView={() => document.querySelector('[data-vendor-watch-section]')?.scrollIntoView({ block: 'start', behavior: 'smooth' })}
                            onAlertDismiss={async (alert) => {
                              const { data: userData } = await supabase.auth.getUser();
                              const email = userData?.user?.email || null;
                              const nowIso = new Date().toISOString();
                              const cooldownIso = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
                              await supabase.from('shrinkflation_alerts').update({
                                status: 'dismissed', dismissed_at: nowIso, dismissed_by: email,
                                cooled_down_until: cooldownIso, updated_at: nowIso,
                              }).eq('id', alert.id);
                              fetchAlerts();
                            }}
                            onNeedsReviewReview={() => document.querySelector('[data-data-review-section]')?.scrollIntoView({ block: 'start', behavior: 'smooth' })}
                            onNeedsReviewAccept={async (reviewRow) => {
                              const { acceptParseSuggestion } = await import('./lib/parseGuardrails');
                              const { data: userData } = await supabase.auth.getUser();
                              const email = userData?.user?.email || null;
                              await acceptParseSuggestion({ companyId, foodItemId: reviewRow.id, userEmail: email });
                              fetchReviewItems();
                              fetchData();
                            }}
                          />
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
            {!isMobile && viewMode === 'price' && (loading || filteredItems.length > 0) && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wide">Item</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wide">Category</th>
                      <th className="text-right px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wide">Price</th>
                      <th className="text-right px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wide">Previous</th>
                      <th className="text-center px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wide">Change</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wide">Trend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      [...Array(5)].map((_, i) => <ShimmerRow key={i} />)
                    ) : (
                      filteredItems.map(item => (
                        <ItemRow
                          key={item.id}
                          item={item}
                          expanded={expandedItem === item.id}
                          onToggleExpand={() => setExpandedItem(expandedItem === item.id ? null : item.id)}
                          priceHistory={rangedPriceHistory[item.id]}
                          regional={regionalData[item.normalized_name]}
                          companyRegion={companyRegion}
                          foodItems={foodItems}
                          activeAlert={activeAlertByItemId[item.id]}
                          needsReviewItem={needsReviewByItemId[item.id]}
                          onAlertView={() => {
                            // Scroll the Vendor Watch card matching this item into view.
                            document.querySelector('[data-vendor-watch-section]')?.scrollIntoView({ block: 'start', behavior: 'smooth' });
                          }}
                          onAlertDismiss={async () => {
                            const alert = activeAlertByItemId[item.id];
                            if (!alert) return;
                            const { data: userData } = await supabase.auth.getUser();
                            const email = userData?.user?.email || null;
                            const nowIso = new Date().toISOString();
                            const cooldownIso = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
                            await supabase.from('shrinkflation_alerts').update({
                              status: 'dismissed',
                              dismissed_at: nowIso,
                              dismissed_by: email,
                              cooled_down_until: cooldownIso,
                              updated_at: nowIso,
                            }).eq('id', alert.id);
                            fetchAlerts();
                          }}
                          onNeedsReviewReview={() => {
                            document.querySelector('[data-data-review-section]')?.scrollIntoView({ block: 'start', behavior: 'smooth' });
                          }}
                          onNeedsReviewAccept={async () => {
                            const reviewRow = needsReviewByItemId[item.id];
                            if (!reviewRow) return;
                            const { acceptParseSuggestion } = await import('./lib/parseGuardrails');
                            const { data: userData } = await supabase.auth.getUser();
                            const email = userData?.user?.email || null;
                            await acceptParseSuggestion({ companyId, foodItemId: reviewRow.id, userEmail: email });
                            fetchReviewItems();
                            fetchData();
                          }}
                          rangeDays={rangeDays}
                          expandedHistoryEntries={expandedHistoryEntries}
                          onToggleHistoryEntry={(key) => setExpandedHistoryEntries(prev => { const next = new Set(prev); next.has(key) ? next.delete(key) : next.add(key); return next; })}
                          editingHistoryEntry={editingHistoryEntry}
                          editPriceValue={editPriceValue}
                          setEditPriceValue={setEditPriceValue}
                          editQtyValue={editQtyValue}
                          setEditQtyValue={setEditQtyValue}
                          editLineTotalValue={editLineTotalValue}
                          setEditLineTotalValue={setEditLineTotalValue}
                          onStartEditEntry={(p, i) => {
                            setEditingHistoryEntry(`${item.id}-${i}`);
                            setEditPriceValue(Number(p.price).toFixed(2));
                            setEditQtyValue(p.quantity != null ? String(Number(p.quantity)) : '');
                            const lt = p.line_total != null
                              ? Number(p.line_total)
                              : (p.quantity != null ? Math.round(Number(p.price) * Number(p.quantity) * 100) / 100 : null);
                            setEditLineTotalValue(lt != null ? lt.toFixed(2) : '');
                          }}
                          onCancelEditEntry={() => { setEditingHistoryEntry(null); setEditPriceValue(''); setEditQtyValue(''); setEditLineTotalValue(''); }}
                          onSaveEditEntry={(p, i) => savePriceOverride(item, p, i)}
                        />
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Card view (mobile) */}
            {isMobile && !loading && filteredItems.length > 0 && (
              <div className="p-3 space-y-2">
                {filteredItems.map(item => (
                  <div key={item.id} className="p-3 border border-gray-100 rounded-xl cursor-pointer" onClick={() => setExpandedItem(expandedItem === item.id ? null : item.id)}>
                    <div className="flex items-start justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-gray-900 text-sm truncate">{item.normalized_name}</div>
                        <div className="flex items-center gap-2 mt-1">
                          {item.category && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{item.category}</span>
                          )}
                          {item.totalSpend90d > 0 && (
                            <span className="text-xs text-gray-400">${item.totalSpend90d.toLocaleString('en-US', { maximumFractionDigits: 0 })} / range</span>
                          )}
                        </div>
                      </div>
                      <div className="text-right ml-3">
                        <div className="text-lg font-bold text-gray-900">
                          {item.current_price != null ? `$${Number(item.current_price).toFixed(2)}` : '--'}
                        </div>
                        <div className="flex items-center gap-1.5 justify-end">
                          <PriceChangePill pct={item.price_change_pct} />
                          {item.dollarImpact !== 0 && (
                            <span className={`text-xs font-bold ${item.dollarImpact > 0 ? 'text-red-600' : 'text-green-600'}`}>
                              {item.dollarImpact > 0 ? '+' : '-'}${Math.abs(item.dollarImpact).toFixed(0)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {expandedItem === item.id && rangedPriceHistory[item.id] && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <div className="bg-white rounded-xl border border-gray-200 p-3">
                          <TrendChart data={rangedPriceHistory[item.id]} width={300} height={140} />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {!loading && foodItems.length > 0 && filteredItems.length === 0 && (
              <div className="py-8 text-center text-sm text-gray-400">No items match your search</div>
            )}
          </div>
        </div>

        {/* Recent Invoices Sidebar */}
        {sidebarOpen && (
        <div className={isMobile ? 'w-full' : 'w-80 flex-shrink-0'}>
          <div className="bg-white rounded-2xl border border-gray-200" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900">Recent Invoices</h2>
              {!isMobile && (
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100"
                  title="Collapse sidebar"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
                </button>
              )}
            </div>
            {invoices.length === 0 ? (
              <div className="p-4 text-sm text-gray-400 text-center">No invoices uploaded yet</div>
            ) : (
              <div className="divide-y divide-gray-50">
                {invoices.map(inv => (
                  <button
                    key={inv.id}
                    onClick={() => viewInvoiceDetail(inv)}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center justify-between"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{inv.vendor_name || 'Unknown vendor'}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{inv.invoice_date || 'No date'}</div>
                    </div>
                    <div className="text-right ml-3 flex-shrink-0">
                      <StatusBadge status={inv.status} />
                      {inv.total_amount && (
                        <div className="text-xs text-gray-400 mt-1">${Number(inv.total_amount).toFixed(2)}</div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        )}
      </div>

      {/* ── Upload Invoice Modal ── */}
      {showUpload && (
        <div className="fixed inset-0 z-50 flex items-center justify-center modal-backdrop" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto modal-content" style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold text-gray-900">Upload Invoice</h2>
              </div>
              <button onClick={resetUploadModal} className="text-gray-400 hover:text-gray-600 text-lg">&times;</button>
            </div>
            <div className="p-6">
              {/* Mode toggle */}
              {!parsedItems && (
                <div className="flex gap-1 rounded-lg p-1 mb-4" style={{ background: 'var(--bg-hover)' }}>
                  <button onClick={() => { setUploadMode('pdf'); setUploadFile(null); }} className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${uploadMode === 'pdf' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}>
                    PDF / Image
                  </button>
                  <button onClick={() => { setUploadMode('csv'); setUploadFile(null); }} className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${uploadMode === 'csv' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}>
                    CSV / Excel
                  </button>
                </div>
              )}

              {/* Batch upload queue — appears when multiple files were selected / dropped / pasted. */}
              {!parsedItems && uploadMode === 'pdf' && batchJobs.length > 0 && (() => {
                const counts = batchJobs.reduce((acc, j) => { acc[j.status] = (acc[j.status] || 0) + 1; return acc; }, {});
                const done = (counts.saved || 0) + (counts.failed || 0) + (counts.skipped || 0);
                const total = batchJobs.length;
                const statusPill = (s) => {
                  const map = {
                    queued:   { label: 'Queued',  cls: 'bg-gray-100 text-gray-500' },
                    parsing:  { label: 'Parsing', cls: 'bg-blue-50 text-blue-600' },
                    saving:   { label: 'Saving',  cls: 'bg-blue-50 text-blue-600' },
                    saved:    { label: 'Saved',   cls: 'bg-green-50 text-green-700' },
                    skipped:  { label: 'Duplicate — skipped', cls: 'bg-amber-50 text-amber-700' },
                    failed:   { label: 'Failed',  cls: 'bg-red-50 text-red-600' },
                  };
                  const { label, cls } = map[s] || { label: s, cls: 'bg-gray-100 text-gray-500' };
                  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cls}`}>{label}</span>;
                };
                return (
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-gray-900">Batch upload ({done}/{total})</h3>
                      <div className="text-xs text-gray-400">Up to {BATCH_CONCURRENCY} parsing in parallel</div>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5 mb-4 overflow-hidden">
                      <div className="bg-gray-900 h-1.5 rounded-full transition-all" style={{ width: `${total > 0 ? (done / total) * 100 : 0}%` }} />
                    </div>
                    <div className="border border-gray-200 rounded-xl divide-y divide-gray-100 max-h-80 overflow-y-auto">
                      {batchJobs.map(job => (
                        <div key={job.id} className="flex items-center justify-between gap-3 px-3 py-2">
                          <div className="min-w-0 flex-1">
                            <div className="text-sm text-gray-900 truncate" title={job.file.name}>{job.file.name}</div>
                            <div className="text-xs text-gray-400 truncate">
                              {job.status === 'parsing' && (job.statusText || 'Parsing…')}
                              {job.status === 'saving'  && 'Saving items…'}
                              {job.status === 'saved'   && `${job.itemCount || 0} items saved`}
                              {job.status === 'skipped' && job.error}
                              {job.status === 'failed'  && job.error}
                              {job.status === 'queued'  && 'Waiting…'}
                            </div>
                          </div>
                          {statusPill(job.status)}
                          {job.status === 'failed' && (
                            <button onClick={() => retryBatchJob(job.id)} className="text-xs hover:underline font-medium" style={{ color: 'var(--accent-primary)', background: 'transparent', border: 'none', cursor: 'pointer' }}>Retry</button>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center gap-3 mt-4">
                      <button onClick={resetUploadModal} disabled={batchRunning}
                        className="flex-1 py-2.5 text-sm font-medium text-white rounded-lg disabled:opacity-50"
                        style={{ background: 'var(--text-primary)', color: 'var(--bg-card)', borderRadius: 10 }}>
                        {batchRunning ? 'Processing…' : 'Done'}
                      </button>
                      {!batchRunning && (
                        <button onClick={() => { setBatchJobs([]); setParseError(''); }}
                          className="px-4 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
                          style={{ borderRadius: 10 }}>
                          Upload more
                        </button>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Drop zone */}
              {!parsedItems && uploadMode === 'pdf' && batchJobs.length === 0 && (
                <>
                  <div
                    ref={dropRef}
                    onDragOver={e => e.preventDefault()}
                    onDrop={handleFileDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center cursor-pointer hover:border-gray-400 transition-colors mb-4"
                  >
                    <svg className="mx-auto mb-3 text-gray-300" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/></svg>
                    <p className="text-sm text-gray-600 font-medium">
                      {uploadFile ? uploadFile.name : 'Drop invoice here, click to browse, or paste a screenshot'}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">PDF, JPG, PNG, HEIC (max 10MB) — Ctrl+V / Cmd+V to paste. Pick multiple files to batch upload.</p>
                    <input ref={fileInputRef} type="file" multiple className="hidden" accept=".pdf,.jpg,.jpeg,.png,.heic" onChange={handleFileSelect} />
                  </div>

                  {/* Optional fields */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Vendor (optional)</label>
                      <input
                        type="text"
                        list="upload-vendor-list"
                        value={uploadVendor}
                        onChange={e => setUploadVendor(e.target.value)}
                        placeholder="Pick existing or type new — leave blank to auto-detect"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-gray-900"
                        style={{ borderRadius: 8 }}
                      />
                      <datalist id="upload-vendor-list">
                        {knownVendors.map(v => <option key={v} value={v} />)}
                      </datalist>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Invoice Date (optional)</label>
                      <input
                        type="date"
                        value={uploadDate}
                        onChange={e => setUploadDate(e.target.value)}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-gray-900"
                        style={{ borderRadius: 8 }}
                      />
                    </div>
                  </div>

                  {/* Parse button */}
                  {uploadFile && !parsing && (
                    <button
                      onClick={parseInvoice}
                      className="w-full py-2.5 text-sm font-medium text-white rounded-lg"
                      style={{ background: 'var(--text-primary)', color: 'var(--bg-card)', borderRadius: 10 }}
                    >
                      Parse Invoice
                    </button>
                  )}

                  {/* Parsing progress */}
                  {parsing && (
                    <div className="flex items-center gap-3 py-3">
                      <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin" />
                      <span className="text-sm text-gray-600">{parseStep}</span>
                    </div>
                  )}
                </>
              )}

              {/* CSV/Excel drop zone */}
              {!parsedItems && uploadMode === 'csv' && (
                <>
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center cursor-pointer hover:border-gray-400 transition-colors mb-4"
                  >
                    <svg className="mx-auto mb-3 text-gray-300" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                    <p className="text-sm text-gray-600 font-medium">
                      {uploadFile ? uploadFile.name : 'Click to select CSV or Excel file'}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">.csv, .xlsx, .xls — exported from your vendor's portal</p>
                    <input ref={fileInputRef} type="file" className="hidden" accept=".csv,.xlsx,.xls" onChange={handleFileSelect} />
                  </div>

                  {/* Vendor & date */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Vendor</label>
                      <input type="text" list="upload-vendor-list-csv"
                        value={uploadVendor} onChange={e => setUploadVendor(e.target.value)}
                        placeholder="Pick existing or type new"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-gray-900" style={{ borderRadius: 8 }} />
                      <datalist id="upload-vendor-list-csv">
                        {knownVendors.map(v => <option key={v} value={v} />)}
                      </datalist>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Invoice Date</label>
                      <input type="date" value={uploadDate} onChange={e => setUploadDate(e.target.value)}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-gray-900" style={{ borderRadius: 8 }} />
                    </div>
                  </div>

                  {uploadFile && !parsing && (
                    <button onClick={parseCSV} className="w-full py-2.5 text-sm font-medium text-white rounded-lg" style={{ background: 'var(--text-primary)', color: 'var(--bg-card)', borderRadius: 10 }}>
                      Import Spreadsheet
                    </button>
                  )}

                  {parsing && (
                    <div className="flex items-center gap-3 py-3">
                      <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin" />
                      <span className="text-sm text-gray-600">{parseStep}</span>
                    </div>
                  )}
                </>
              )}

              {/* Error state */}
              {parseError && (
                <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-100">
                  <p className="text-sm text-red-600 font-medium">Error: {parseError}</p>
                  {rawResponse && (
                    <details className="mt-2">
                      <summary className="text-xs text-red-400 cursor-pointer">View raw response</summary>
                      <pre className="mt-1 text-xs text-gray-600 whitespace-pre-wrap max-h-40 overflow-y-auto bg-white p-2 rounded">{rawResponse}</pre>
                    </details>
                  )}
                  <button
                    onClick={() => { setParseError(''); setParsedItems(null); }}
                    className="mt-2 text-sm text-red-600 font-medium hover:underline"
                  >
                    Try Again
                  </button>
                </div>
              )}

              {/* Parsed items review table */}
              {parsedItems && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-900">
                      {parsedItems.length} items found
                      {parsedItems.some(i => i._packVerified) && <span className="text-xs font-normal text-green-600 ml-2">({parsedItems.filter(i => i._packVerified).length} verified by prior invoices)</span>}
                    </h3>
                    <span className="text-xs text-gray-400">Edit names/categories before saving</span>
                  </div>
                  <div className="overflow-x-auto border border-gray-200 rounded-xl">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-100">
                          <th className="text-left px-3 py-2 text-xs font-medium text-gray-400">Description</th>
                          <th className="text-left px-3 py-2 text-xs font-medium text-gray-400">Code</th>
                          <th className="text-right px-3 py-2 text-xs font-medium text-gray-400">Qty</th>
                          <th className="text-left px-3 py-2 text-xs font-medium text-gray-400">Unit</th>
                          <th className="text-right px-3 py-2 text-xs font-medium text-gray-400">Price</th>
                          <th className="text-left px-3 py-2 text-xs font-medium text-gray-400">Normalized Name</th>
                          <th className="text-left px-3 py-2 text-xs font-medium text-gray-400">Category</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsedItems.map((item, idx) => (
                          <tr key={idx} className="border-b border-gray-50">
                            <td className="px-3 py-2 text-gray-600 max-w-[150px] truncate" title={item.raw_description}>{item.raw_description}</td>
                            <td className="px-3 py-2 text-gray-400 font-mono text-xs">{item.product_code || '--'}</td>
                            <td className="px-3 py-2 text-right text-gray-600">{item.quantity ?? '--'}</td>
                            <td className="px-3 py-2 text-gray-500">{item.unit || '--'}</td>
                            <td className="px-3 py-2 text-right font-medium text-gray-900">
                              {item.case_price != null ? `$${Number(item.case_price).toFixed(2)}` : (item.unit_price != null ? `$${Number(item.unit_price).toFixed(2)}` : '--')}
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                value={item.normalized_name || ''}
                                onChange={e => {
                                  const updated = [...parsedItems];
                                  updated[idx] = { ...updated[idx], normalized_name: e.target.value };
                                  setParsedItems(updated);
                                }}
                                className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:border-gray-900"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <select
                                value={item.category || 'Other'}
                                onChange={e => {
                                  const updated = [...parsedItems];
                                  updated[idx] = { ...updated[idx], category: e.target.value };
                                  setParsedItems(updated);
                                }}
                                className="border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:border-gray-900"
                              >
                                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                              </select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex items-center gap-3 mt-4">
                    <button
                      onClick={saveItems}
                      disabled={saving}
                      className="flex-1 py-2.5 text-sm font-medium text-white rounded-lg disabled:opacity-50"
                      style={{ background: 'var(--text-primary)', color: 'var(--bg-card)', borderRadius: 10 }}
                    >
                      {saving ? 'Saving...' : 'Confirm & Save'}
                    </button>
                    <button
                      onClick={resetUploadModal}
                      className="px-4 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
                      style={{ borderRadius: 10 }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Invoice Detail Modal ── */}
      {selectedInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center modal-backdrop" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto modal-content" style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            <div className="p-6 border-b border-gray-100 flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-base font-semibold text-gray-900">{selectedInvoice.vendor_name || 'Invoice'}</h2>
                </div>
                <p className="text-xs text-gray-400 mt-0.5">
                  {selectedInvoice.invoice_date} {selectedInvoice.invoice_number ? ` #${selectedInvoice.invoice_number}` : ''}
                </p>
                {/* Vendor editor — fixes mis-parsed names after the fact so the vendor list stays clean. */}
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <label className="text-xs text-gray-500">Vendor:</label>
                  <input
                    type="text"
                    list="invoice-detail-vendor-list"
                    defaultValue={selectedInvoice.vendor_name || ''}
                    onBlur={(e) => {
                      const next = e.target.value.trim();
                      if (next !== (selectedInvoice.vendor_name || '').trim()) {
                        updateInvoiceVendor(selectedInvoice, next);
                      }
                    }}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                    placeholder="Pick existing or type new"
                    className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-gray-900"
                    style={{ borderRadius: 8 }}
                  />
                  <datalist id="invoice-detail-vendor-list">
                    {knownVendors.map(v => <option key={v} value={v} />)}
                  </datalist>
                  <span className="text-xs text-gray-400">Saved on blur</span>
                </div>
              </div>
              <button onClick={() => { setSelectedInvoice(null); setInvoiceLineItems([]); }} className="text-gray-400 hover:text-gray-600 text-lg">&times;</button>
            </div>
            <div className="p-6">
              {invoiceLineItems.length === 0 ? (
                <div className="text-sm text-gray-400 text-center py-8">No line items found</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th className="text-left px-3 py-2 text-xs font-medium text-gray-400">Item</th>
                        <th className="text-right px-3 py-2 text-xs font-medium text-gray-400">Qty</th>
                        <th className="text-left px-3 py-2 text-xs font-medium text-gray-400">Unit</th>
                        <th className="text-right px-3 py-2 text-xs font-medium text-gray-400">Unit Price</th>
                        <th className="text-right px-3 py-2 text-xs font-medium text-gray-400">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoiceLineItems.map(li => (
                        <tr key={li.id} className="border-b border-gray-50">
                          <td className="px-3 py-2 text-gray-900">{li.normalized_name || li.raw_description}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{li.quantity ?? '--'}</td>
                          <td className="px-3 py-2 text-gray-500">{li.unit || '--'}</td>
                          <td className="px-3 py-2 text-right font-medium">{li.unit_price != null ? `$${Number(li.unit_price).toFixed(2)}` : '--'}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{li.line_total != null ? `$${Number(li.line_total).toFixed(2)}` : '--'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="mt-4 pt-4 border-t border-gray-100 flex justify-end">
                <button
                  onClick={() => deleteInvoice(selectedInvoice)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                  Delete Invoice
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── AI Price Check Modal ── */}
      {showPriceCheck && (
        <div className="fixed inset-0 z-50 flex items-center justify-center modal-backdrop" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto modal-content" style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2"><path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/></svg>
                <h2 className="text-base font-semibold text-gray-900">AI Price Check</h2>
              </div>
              <button onClick={() => setShowPriceCheck(false)} className="text-gray-400 hover:text-gray-600 text-lg">&times;</button>
            </div>
            <div className="p-6">
              {priceCheckLoading ? (
                <div className="flex flex-col items-center py-12">
                  <div className="w-8 h-8 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin mb-4" />
                  <p className="text-sm text-gray-500">Analyzing your price data...</p>
                </div>
              ) : (
                <div>
                  {/* Regional overpaying alerts */}
                  {(() => {
                    const overItems = foodItems
                      .map(fi => {
                        const rd = regionalData[fi.normalized_name];
                        if (!rd || !rd.isActive || rd.diff <= 2) return null;
                        const spend90 = itemSpend[fi.id] || 0;
                        const costDiff = spend90 > 0 ? (rd.diff / 100) * spend90 : 0;
                        return { name: fi.normalized_name, diff: rd.diff, yourPrice: rd.yourPrice, avg: rd.avg, costDiff, spend90 };
                      })
                      .filter(Boolean)
                      .sort((a, b) => b.costDiff - a.costDiff);
                    if (overItems.length === 0) return null;
                    return (
                      <div className="mb-5">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center">
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2.5"><path d="M12 9v4m0 4h.01M12 2a10 10 0 100 20 10 10 0 000-20z"/></svg>
                          </div>
                          <span className="text-sm font-semibold text-gray-900">Above Regional Average</span>
                        </div>
                        <div className="space-y-2">
                          {overItems.map((oi, i) => (
                            <div key={i} className="flex items-center justify-between bg-red-50 border border-red-100 rounded-xl px-4 py-2.5">
                              <div className="min-w-0">
                                <span className="text-sm font-medium text-gray-900">{oi.name}</span>
                                <div className="text-xs text-gray-500 mt-0.5">
                                  You: ${oi.yourPrice?.toFixed(2)} vs avg: ${oi.avg.toFixed(2)}
                                </div>
                              </div>
                              <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                                <span className="text-xs font-bold text-white bg-red-500 px-2.5 py-1 rounded-lg">
                                  +{oi.diff.toFixed(1)}%
                                </span>
                                {oi.costDiff > 0 && (
                                  <span className="text-xs font-semibold text-red-600">
                                    -${oi.costDiff.toFixed(0)}/90d
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                  <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {priceCheckResult.split('\n').map((line, i) => {
                      if (line.startsWith('##') || line.startsWith('**')) {
                        return <p key={i} className="font-semibold text-gray-900 mt-4 mb-1">{line.replace(/[#*]/g, '').trim()}</p>;
                      }
                      if (line.startsWith('- ') || line.startsWith('* ')) {
                        return <p key={i} className="ml-4 text-gray-600">{line}</p>;
                      }
                      return line.trim() ? <p key={i} className="text-gray-600 mb-1">{line}</p> : <br key={i} />;
                    })}
                  </div>
                  {priceCheckMeta.invoices > 0 && (
                    <div className="mt-6 pt-4 border-t border-gray-100 text-xs text-gray-400 text-center">
                      Data based on {priceCheckMeta.invoices} invoices across {priceCheckMeta.weeks} weeks
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Price Tracker Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center modal-backdrop" style={{ background: 'rgba(0,0,0,0.4)' }} onClick={() => setShowSettings(false)}>
          <div className="bg-white rounded-2xl w-full max-w-lg modal-content" style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }} onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-900">Price Tracker Settings</h2>
              <button onClick={() => setShowSettings(false)} className="text-gray-400 hover:text-gray-600 text-lg">&times;</button>
            </div>
            <div className="p-6 space-y-6">
              {/* Sync toggle */}
              <div>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-gray-900">Push invoices to Food Cost</div>
                    <div className="text-xs text-gray-500 mt-1 leading-relaxed">
                      When on, every invoice you upload in Price Tracker also counts toward your Food Cost totals on the Food Cost tab.
                      When off, Food Cost only shows manual entries you add there.
                    </div>
                  </div>
                  <button
                    onClick={() => savePriceTrackerSyncEnabled(!priceTrackerSyncEnabled)}
                    className="flex-shrink-0 relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
                    style={{ background: priceTrackerSyncEnabled ? 'var(--text-primary)' : 'var(--border)' }}
                    aria-pressed={priceTrackerSyncEnabled}
                  >
                    <span
                      className="inline-block h-4 w-4 transform rounded-full bg-white transition-transform"
                      style={{ transform: priceTrackerSyncEnabled ? 'translateX(24px)' : 'translateX(4px)' }}
                    />
                  </button>
                </div>
              </div>

              {/* Cleanup */}
              <div className="pt-6 border-t border-gray-100">
                <div className="text-sm font-medium text-gray-900">Clean up synced Food Cost rows</div>
                <div className="text-xs text-gray-500 mt-1 leading-relaxed">
                  Removes rows on the Food Cost tab that were synced from Price Tracker but no longer match a current Price Tracker invoice
                  (orphans from deletes, duplicates from re-imports). Manual Food Cost entries are not touched.
                </div>
                <div className="flex items-center gap-3 mt-3">
                  <button
                    onClick={cleanupSyncedInvoices}
                    disabled={cleanupStatus === 'running'}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                    style={{ borderRadius: 8 }}
                  >
                    {cleanupStatus === 'running' ? 'Cleaning...' : 'Clean up synced invoices'}
                  </button>
                  {cleanupStatus && cleanupStatus !== 'running' && (
                    <span className="text-xs text-gray-500">{cleanupStatus}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Item Detail Modal — opened from Price Impact card contributors. Uses the range-aware
         summary fields so the modal stays consistent with the items table behind it. */}
      {(() => {
        const modalItem = detailModalItemId
          ? foodItems.map(fi => {
              const rs = rangeStats[fi.id];
              const spendInRange = rangeSpend[fi.id] || 0;
              const pct = rs?.priceChangePct;
              return {
                ...fi,
                current_price: rs?.currentPrice != null ? rs.currentPrice : fi.current_price,
                previous_price: rs?.previousPrice != null ? rs.previousPrice : null,
                price_per_lb: rs?.pricePerLb != null ? rs.pricePerLb : fi.price_per_lb,
                price_change_pct: pct != null ? pct : null,
                dollarImpact: (pct != null && spendInRange) ? (pct / 100) * spendInRange : 0,
                totalSpend90d: spendInRange,
              };
            }).find(fi => fi.id === detailModalItemId)
          : null;
        return (
          <ItemDetailModal
            open={!!modalItem}
            item={modalItem}
            onClose={() => setDetailModalItemId(null)}
            priceHistory={modalItem ? rangedPriceHistory[modalItem.id] : null}
            regional={modalItem ? regionalData[modalItem.normalized_name] : null}
            companyRegion={companyRegion}
            foodItems={foodItems}
            rangeDays={rangeDays}
            expandedHistoryEntries={expandedHistoryEntries}
            onToggleHistoryEntry={(key) => setExpandedHistoryEntries(prev => { const next = new Set(prev); next.has(key) ? next.delete(key) : next.add(key); return next; })}
            editingHistoryEntry={editingHistoryEntry}
            editPriceValue={editPriceValue}
            setEditPriceValue={setEditPriceValue}
            editQtyValue={editQtyValue}
            setEditQtyValue={setEditQtyValue}
            editLineTotalValue={editLineTotalValue}
            setEditLineTotalValue={setEditLineTotalValue}
            onStartEditEntry={(p, i) => {
              if (!modalItem) return;
              setEditingHistoryEntry(`${modalItem.id}-${i}`);
              setEditPriceValue(Number(p.price).toFixed(2));
              setEditQtyValue(p.quantity != null ? String(Number(p.quantity)) : '');
              const lt = p.line_total != null
                ? Number(p.line_total)
                : (p.quantity != null ? Math.round(Number(p.price) * Number(p.quantity) * 100) / 100 : null);
              setEditLineTotalValue(lt != null ? lt.toFixed(2) : '');
            }}
            onCancelEditEntry={() => { setEditingHistoryEntry(null); setEditPriceValue(''); setEditQtyValue(''); setEditLineTotalValue(''); }}
            onSaveEditEntry={(p, i) => { if (modalItem) savePriceOverride(modalItem, p, i); }}
          />
        );
      })()}

      {/* Volume detail drawer (Volume mode only). Slides in from the right
          with observable signals explaining the volume change. See
          DESIGN_SYSTEM.md §Volume detail drawer. */}
      <VolumeDetailDrawer
        open={!!volumeDrawer}
        onClose={() => setVolumeDrawer(null)}
        companyId={companyId}
        item={volumeDrawer?.item || null}
        range={priceImpactRange}
        useWeight={!!volumeDrawer?.useWeight}
      />
    </div>
  );
}
